/**
 * The guide pages' words. Every sentence must hold in the code today; the
 * code each page rests on is named above it. Plain text only: the FAQ
 * answers feed FAQPage JSON-LD word for word. Prices and limits come from
 * the plan config, never typed in.
 */
import { formatCzk, getPlan } from "@/lib/pricing/plans";
import type { Guide, GuideKey } from "./types";

const start = getPlan("start");
const firma = getPlan("firma");
const trialDays = start.trialDays ?? 0;

export const GUIDES: Record<GuideKey, Guide> = {
  // For accountants. Built only from what ships: ReceiptsBundleButton, FioStatementButton,
  // TransactionImportExport (CSV), ExportSection (XLSX, CSV, ISDOC), ISDOCImport (#122, issued
  // invoices, Firma), MissingDocuments (#130), weekly-missing-documents (#128, owners and
  // admins), process-inbox-email, OrganizationMembersSection + WorkspaceSwitcher, mcp.
  // POHODA ZIP export (#136, docs/exports/POHODA.md) is live and described below.
  // Direct send via POHODA mServer (#140) is live: PohodaConnectionSection, pohodaMserver.ts.
  // Shoptet: ISDOC ZIP import (#139) and API sync (#146, EshopConnectionsSection, eshop-sync)
  // are live and described below. TODO(upgates): add once the Upgates API ships.
  // Comgate/Zásilkovna payout reports (#142) are live and described below (PayoutImport).
  // Until then, claim neither anywhere on this page.
  guideAccountants: {
    key: "guideAccountants",
    updated: "2026-11-04",
    seo: {
      title: {
        cs: "Zettl pro účetní: doklady od klientů a platby z Fio na jednom místě",
        en: "Zettl for accountants: client documents and Fio payments in one place",
      },
      description: {
        cs: "Klient přeposílá doklady na adresu firmy, Zettl je páruje s platbami z Fio a vy vidíte, co chybí. Za měsíc PDF s doklady, výpis z Fio, CSV a ISDOC.",
        en: "Your client forwards documents to the company address, Zettl matches them with Fio payments and you see what's missing. Each month: a PDF of documents, the Fio statement, CSV and ISDOC.",
      },
    },
    name: { cs: "Pro účetní", en: "For accountants" },
    eyebrow: { cs: "Pro účetní", en: "For accountants" },
    h1: {
      cs: "Doklady od klientů a jejich platby z Fio na jednom místě",
      en: "Your clients' documents and their Fio payments in one place",
    },
    lead: {
      cs: "Klient doklady přeposílá na e-mailovou adresu své firmy v Zettlu, Zettl je páruje s platbami z jeho účtu u Fio a vy v každé chvíli vidíte, které platby ještě nemají doklad. Za měsíc si stáhnete doklady v jednom PDF s přehledem, výpis z Fio a platby v CSV.",
      en: "Your client forwards documents to their company's Zettl e-mail address, Zettl matches them with the payments on their Fio account, and you can see at any time which payments still lack a document. For each month you download the documents as one PDF with a summary, the Fio statement and the payments as CSV.",
    },
    visual: "accountants",
    steps: [
      {
        title: {
          cs: "Klient si založí Zettl a pozve vás",
          en: "Your client sets up Zettl and invites you",
        },
        text: {
          cs: `Zaregistruje se, připojí účet u Fio a prvních ${trialDays} dní má zdarma. Pak vás pozve e-mailem do své firmy v Zettlu.`,
          en: `They sign up, connect their Fio account and get the first ${trialDays} days free. Then they invite you to their company in Zettl by e-mail.`,
        },
      },
      {
        title: { cs: "Doklady chodí, Zettl je páruje", en: "Documents arrive, Zettl matches them" },
        text: {
          cs: "Klient přeposílá faktury a účtenky na adresu své firmy @zettl.cz, AI z nich přečte dodavatele, datum a částku a Zettl je páruje s platbami z Fio.",
          en: "The client forwards invoices and receipts to their company's @zettl.cz address; AI reads the vendor, date and amount, and Zettl matches them with Fio payments.",
        },
      },
      {
        title: { cs: "Hotový měsíc předáte dál", en: "You pass the finished month on" },
        text: {
          cs: "Doklady za měsíc v jednom PDF s přehledem, výpis z Fio v PDF, platby jako CSV, vydané faktury jako XLSX, CSV nebo ISDOC a pro POHODU ZIP s XML, nebo rovnou přes mServer. Klient může zapnout, aby vám Zettl měsíc posílal e-mailem sám.",
          en: "The month's documents as one PDF with a summary, the Fio statement as PDF, payments as CSV, issued invoices as XLSX, CSV or ISDOC, and for POHODA a ZIP of XML, or straight in through mServer. The client can have Zettl e-mail you the month by itself.",
        },
      },
    ],
    features: [
      {
        icon: "missing",
        title: { cs: "Chybějící doklady", en: "Missing documents" },
        text: {
          cs: "Odchozí platby bez dokladu na jedné stránce. Doklad přiložíte, nebo platbu označíte, že doklad nepotřebuje; bankovní poplatky, platby daní a pojištění na účty u ČNB a převody mezi vlastními účty navrhne Zettl sám.",
          en: "Outgoing payments without a document on one page. Attach one, or mark that none is needed; Zettl itself suggests bank fees, tax and insurance payments to accounts at the ČNB, and transfers between own accounts.",
        },
      },
      {
        icon: "mail",
        title: { cs: "Pondělní připomínka", en: "A Monday reminder" },
        text: {
          cs: "Vlastník a správci firmy dostanou v pondělí e-mail s počtem plateb bez dokladu za uplynulý týden a odkazem, kde je doplnit.",
          en: "On Monday the company's owner and admins get an e-mail with the number of last week's payments without a document and a link to fill them in.",
        },
      },
      {
        icon: "send",
        title: { cs: "POHODA: ZIP nebo mServer", en: "POHODA: ZIP or mServer" },
        text: {
          cs: "Přijaté doklady a pohyby za měsíc jako XML pro import do POHODY, nebo je Zettl pošle přímo přes mServer, je-li dostupný z internetu přes HTTPS.",
          en: "The month's received documents and movements as XML for import into POHODA, or Zettl sends them straight in through mServer if it is reachable from the internet over HTTPS.",
        },
      },
      {
        icon: "isdoc",
        title: { cs: "ISDOC tam i zpět", en: "ISDOC both ways" },
        text: {
          cs: `Vydané faktury exportujete do ISDOC a importujete ze souborů ISDOC (.isdoc, .isdocx, více najednou nebo jako ZIP), s náhledem, dohledáním odběratele podle IČO a přeskočením faktur, které už v Zettlu jsou. Import faktur je v tarifu ${firma.name}.`,
          en: `Export issued invoices to ISDOC and import them from ISDOC files (.isdoc, .isdocx, several at once or as a ZIP), with a preview, the customer found by company ID, and invoices already in Zettl skipped. Invoice import is part of the ${firma.name} plan.`,
        },
      },
      {
        icon: "shop",
        title: { cs: "E-shop na Shoptetu", en: "Shoptet e-shops" },
        text: {
          cs: "S tarifem Shoptet Premium přicházejí vydané faktury automaticky každou noc přes soukromé API Shoptetu; s ostatními tarify je klient nahraje jako ZIP z exportu ISDOC. Příchozí platby z Fio se k nim spárují podle variabilního symbolu.",
          en: "With the Shoptet Premium plan, issued invoices come in automatically every night through Shoptet's private API; on other plans the client uploads them as a ZIP of the ISDOC export. Incoming Fio payments are matched to them by variable symbol.",
        },
      },
      {
        icon: "payout",
        title: { cs: "Výplaty Comgate a Zásilkovny", en: "Comgate and Packeta payouts" },
        text: {
          cs: "Z výplatního reportu (CSV nebo XLSX) Zettl spojí každou výplatu s příchozí platbou na Fio, faktury, které pokrývá, označí jako zaplacené a zaznamená hrubou částku, poplatek i čistou výplatu.",
          en: "From the payout report (CSV or XLSX), Zettl links each payout to the incoming Fio payment, marks the invoices it covers as paid, and records the gross amount, the fee and the net payout.",
        },
      },
      {
        icon: "companies",
        title: { cs: "Všichni klienti na jedné stránce", en: "Every client on one page" },
        text: {
          cs: "Jste-li členem firem několika klientů, uvidíte je za zvolený měsíc vedle sebe: kolik plateb nemá doklad, stav Fio tokenu, poslední data a odeslání do POHODY. Tlačítkem Připomenout klientovi na stránce Klienti pošlete vlastníkovi a správcům firmy e-mail s platbami bez dokladu, nejvýš jednou denně.",
          en: "If you're a member of several clients' companies, you see them side by side for the chosen month: how many payments lack a document, the Fio token's health, the latest data and POHODA sends. On the Clients page, Remind the client e-mails the company's owner and admins the payments without a document, at most once a day.",
        },
      },
      {
        icon: "export",
        title: { cs: "Podklady e-mailem každý měsíc", en: "The month by e-mail, every month" },
        text: {
          cs: "Vlastník nebo správce firmy klienta zapne v Nastavení → Účetnictví den v měsíci (1.–28.) a až pět adres. Ten den Zettl pošle odkaz na ZIP za předchozí měsíc: výpis z účtu jako CSV, soubory dokladů a XML pro POHODU. Odkaz platí 14 dní.",
          en: "The client's owner or an admin picks a day of the month (1st–28th) and up to five addresses in Settings → Accounting. On that day Zettl sends a link to a ZIP for the previous month: the account statement as CSV, the documents' files and the XML for POHODA. The link works for 14 days.",
        },
      },
      {
        icon: "assistant",
        title: { cs: "AI asistent přes MCP", en: "An AI assistant via MCP" },
        text: {
          cs: `V tarifu ${firma.name} můžete k firmě klienta připojit AI asistenta (např. Claude) a zeptat se třeba, které platby ještě nemají doklad.`,
          en: `On the ${firma.name} plan you can connect an AI assistant (e.g. Claude) to a client's company and ask, for example, which payments still lack a document.`,
        },
      },
    ],
    notes: [
      {
        cs: "Zettl nevede účetnictví. Připraví spárované platby a doklady; zaúčtování děláte ve svém programu.",
        en: "Zettl does not do the bookkeeping. It prepares matched payments and documents; you book them in your own software.",
      },
      {
        cs: "Pondělní e-mail chodí vlastníkovi a správcům firmy. Pokud ho chcete dostávat i vy, musí vás klient přidat jako správce; role správce je v tarifu Firma.",
        en: "The Monday e-mail goes to the company's owner and admins. If you want it too, the client has to add you as an admin; the admin role is part of the Firma plan.",
      },
      {
        cs: "Zettl funguje jen s účty u Fio banky.",
        en: "Zettl works only with accounts at Fio banka.",
      },
    ],
    faq: [
      {
        id: "kdo-plati",
        question: {
          cs: "Kdo za Zettl platí, klient, nebo účetní?",
          en: "Who pays for Zettl, the client or the accountant?",
        },
        answer: {
          cs: `Tarif má firma klienta. Vy se do ní připojíte jako člen týmu a počítáte se mezi její uživatele: tarif ${start.name} (${formatCzk(start.priceMonthlyCzk)} měsíčně) má ${start.limits.users} uživatele, tedy klienta a vás.`,
          en: `The plan belongs to the client's company. You join it as a team member and count among its users: the ${start.name} plan (${formatCzk(start.priceMonthlyCzk)} a month) has ${start.limits.users} users, the client and you.`,
        },
      },
      {
        id: "jak-klienty",
        question: {
          cs: "Jak přimět klienty, aby doklady posílali včas?",
          en: "How do I get clients to send their documents on time?",
        },
        answer: {
          cs: "Doklad stačí přeposlat e-mailem na adresu firmy v Zettlu, nic nepřepisují. Faktury od pravidelných dodavatelů může přeposílat pravidlo v jejich schránce. V pondělí jim Zettl připomene platby bez dokladu a na stránce Chybějící doklady vidí, co ještě dodat. Ze stránky Klienti jim navíc kdykoli pošlete připomínku s platbami bez dokladu za zvolený měsíc.",
          en: "Forwarding the e-mail to the company's Zettl address is all it takes; nothing is retyped. A rule in their mailbox can forward invoices from regular vendors. On Monday Zettl reminds them of payments without a document, and the Missing documents page shows what is still due. From the Clients page you can also send them a reminder of the month's payments without a document at any time.",
        },
      },
      {
        id: "vice-klientu",
        question: {
          cs: "Můžu mít v Zettlu víc klientů?",
          en: "Can I have several clients in Zettl?",
        },
        answer: {
          cs: "Ano. Každý klient vás pozve do své firmy a v aplikaci mezi firmami přepínáte pod jedním přihlášením. Stránka Klienti ukáže všechny firmy za zvolený měsíc na jedné stránce.",
          en: "Yes. Each client invites you to their company, and you switch between companies in the app under one login. The Clients page shows every company for the chosen month on one page.",
        },
      },
      {
        id: "program",
        question: {
          cs: "Jak dostanu data do svého účetního programu?",
          en: "How do I get the data into my accounting software?",
        },
        answer: {
          cs: "Do POHODY Zettl pošle přijaté doklady a bankovní pohyby za měsíc přímo přes mServer, je-li dostupný z internetu přes HTTPS; jinak stáhnete ZIP s XML a soubory dokladů. Vydané faktury jako ISDOC, který načte většina českých účetních programů, nebo jako XLSX či CSV. Platby jako CSV, výpis z Fio jako PDF a přijaté doklady za měsíc jako jeden PDF s přehledem.",
          en: "Zettl sends a month's received documents and bank movements straight into POHODA through mServer, if it is reachable from the internet over HTTPS; otherwise you download a ZIP with the XML and the document files. Issued invoices as ISDOC, which most Czech accounting software reads, or as XLSX or CSV. Payments as CSV, the Fio statement as PDF, and the month's received documents as one PDF with a summary.",
        },
      },
      {
        id: "role",
        question: {
          cs: "Jakou roli mám ve firmě klienta?",
          en: "Which role do I get in the client's company?",
        },
        answer: {
          cs: `Člen týmu: vidí platby a doklady a může je upravovat. V tarifu ${firma.name} vás klient může přidat i jako správce, pak dostáváte pondělní e-mail a můžete spravovat tým.`,
          en: `Team member: you see payments and documents and can edit them. On the ${firma.name} plan the client can add you as an admin, so you get the Monday e-mail and can manage the team.`,
        },
      },
    ],
    related: ["guidePohoda", "guideAccountant", "guideEmailInbox"],
  },

  // POHODA ZIP export (#136): src/lib/exports/pohoda.ts, pohodaExport.ts, PohodaExportCard,
  // docs/exports/POHODA.md. Validated against Stormware's XSDs. Direct send via mServer
  // (#140): PohodaConnectionSection, pohodaMserver.ts; needs mServer reachable over HTTPS.
  guidePohoda: {
    key: "guidePohoda",
    updated: "2026-11-03",
    seo: {
      title: {
        cs: "Zettl a POHODA: doklady a platby z Fio do POHODY — Zettl",
        en: "Zettl and POHODA: documents and Fio payments into POHODA — Zettl",
      },
      description: {
        cs: "Zettl sbírá doklady a páruje je s platbami z Fio. Za měsíc je pošle přímo do POHODY přes mServer, nebo stáhnete ZIP s XML pro import.",
        en: "Zettl collects documents and matches them with Fio payments. Each month it sends them straight into POHODA through mServer, or you download a ZIP of XML for import.",
      },
    },
    name: { cs: "Zettl a POHODA", en: "Zettl and POHODA" },
    eyebrow: { cs: "Pro účetní", en: "For accountants" },
    h1: {
      cs: "Zettl a POHODA: doklady posbírá Zettl, účtuje se v POHODĚ",
      en: "Zettl and POHODA: Zettl collects the documents, POHODA does the books",
    },
    lead: {
      cs: "Zettl POHODU nenahrazuje, doplňuje ji. Posbírá od klienta doklady, spáruje je s platbami z Fio a za měsíc z něj stáhnete jeden ZIP: přijaté doklady a bankovní pohyby jako XML pro import do POHODY a k tomu soubory dokladů. Je-li POHODA mServer dostupný z internetu, pošle Zettl měsíc do POHODY i přímo.",
      en: "Zettl doesn't replace POHODA; it complements it. It collects the client's documents, matches them with Fio payments, and for each month you download one ZIP: received documents and bank movements as XML for import into POHODA, plus the document files. If POHODA mServer is reachable from the internet, Zettl can also send the month into POHODA directly.",
    },
    visual: "pohoda",
    diagram: {
      key: "pohodaFlow",
      title: { cs: "Dvě cesty do POHODY", en: "Two ways into POHODA" },
    },
    // The step-by-step guide with screenshots and troubleshooting comes from the
    // POHODA session's own PR (/navod-pohoda); the orchestrator merges it in an
    // order that keeps this link valid.
    deeperLink: {
      label: { cs: "Podrobný návod krok za krokem", en: "Step-by-step guide" },
      href: { cs: "/navod-pohoda", en: "/en/pohoda-guide" },
    },
    steps: [
      {
        title: { cs: "Doklady a platby v Zettlu", en: "Documents and payments in Zettl" },
        text: {
          cs: "Klient přeposílá doklady na adresu firmy, platby přicházejí z Fio a Zettl je páruje.",
          en: "The client forwards documents to the company address, payments arrive from Fio and Zettl matches them.",
        },
      },
      {
        title: { cs: "Export do POHODY", en: "Export to POHODA" },
        text: {
          cs: "Nastavení → Účetnictví (nebo Transakce → Import / Export) → Export do POHODY: vyberete měsíc a zkratku bankovního účtu v POHODĚ a stáhnete ZIP.",
          en: "Settings → Accounting (or Transactions → Import / Export) → Export to POHODA: pick the month and the bank account's abbreviation in POHODA, and download the ZIP.",
        },
      },
      {
        title: { cs: "Co je v ZIPu", en: "What's in the ZIP" },
        text: {
          cs: "prijate-doklady.xml (přijaté faktury), banka.xml (pohyby na účtu) a složka doklady se soubory. XML je ve verzi 2 a v kódování Windows-1250, jak ho POHODA čte.",
          en: "prijate-doklady.xml (received invoices), banka.xml (account movements) and a doklady folder with the files. The XML is version 2 in Windows-1250, as POHODA reads it.",
        },
      },
      {
        title: { cs: "Nebo rovnou do POHODY", en: "Or straight into POHODA" },
        text: {
          cs: "Je-li POHODA mServer dostupný z internetu přes HTTPS, zadáte jeho adresu, uživatele a heslo v Nastavení → Účetnictví → Napojení na POHODU a měsíc pošlete tlačítkem Odeslat do POHODY. U každého dokladu uvidíte, jestli se naimportoval, už v POHODĚ je, nebo skončil chybou.",
          en: "If POHODA mServer is reachable from the internet over HTTPS, enter its address, user and password under Settings → Accounting → POHODA connection and send the month with Send to POHODA. For each document you see whether it was imported, already exists in POHODA, or failed.",
        },
      },
      {
        title: { cs: "Import a likvidace v POHODĚ", en: "Import and settlement in POHODA" },
        text: {
          cs: "Po importu XML spárujete platby s doklady automatickou likvidací v POHODĚ: spárovaná platba nese párový symbol svého dokladu.",
          en: "After importing the XML, pair payments with documents using POHODA's automatic settlement: a matched payment carries its document's pairing symbol.",
        },
      },
    ],
    notes: [
      {
        cs: "Zettl u přijatých dokladů neukládá rozpis DPH, IČO a DIČ dodavatele ani DUZP. Každý doklad proto přijde do POHODY celkovou částkou bez rozdělení na sazby DPH a rozdělí ho účetní.",
        en: "For received documents Zettl stores no VAT breakdown, supplier IČO/DIČ or taxable supply date. Each document therefore arrives in POHODA as its total, not split by VAT rate, for the accountant to split.",
      },
      {
        cs: "Bankovní pohyby nesou variabilní symbol; konstantní a specifický symbol Zettl neukládá.",
        en: "Bank movements carry the variable symbol; Zettl stores no constant or specific symbol.",
      },
      {
        cs: "Export vyžaduje IČO firmy v nastavení Zettlu: POHODA přijme data jen pro účetní jednotku se stejným IČO.",
        en: "The export needs the company's IČO in Zettl's settings: POHODA only accepts data for the accounting unit with the same IČO.",
      },
      {
        cs: "Přímé odeslání vyžaduje POHODA mServer dostupný z internetu přes HTTPS. POHODA nainstalovaná na počítači v kanceláři takto obvykle dostupná není; pak použijte export do ZIPu. Heslo k mServeru Zettl ukládá šifrovaně a napojení nastavuje vlastník nebo správce firmy.",
        en: "Direct sending needs POHODA mServer reachable from the internet over HTTPS. POHODA installed on an office PC usually isn't; then use the ZIP export. Zettl stores the mServer password encrypted, and the company's owner or an admin sets up the connection.",
      },
      {
        cs: "Opakovaný import téhož měsíce nové doklady přidá a ty, které už v POHODĚ jsou, odmítne. Pohyby importujte buď z banka.xml, nebo z výpisu přímo do POHODY, ne obojí, jinak tam budou dvakrát.",
        en: "Re-importing the same month adds new documents and refuses those already in POHODA. Import movements either from banka.xml or from a statement straight into POHODA, not both, or they will be there twice.",
      },
      {
        cs: "XML je ověřené proti schématům, která zveřejňuje Stormware. Import do POHODY doporučujeme poprvé vyzkoušet na jednom měsíci.",
        en: "The XML is validated against the schemas Stormware publishes. Try the first import into POHODA on a single month.",
      },
      {
        cs: "Vydané faktury tento export neobsahuje; ze Zettlu odcházejí jako ISDOC.",
        en: "Issued invoices are not part of this export; they leave Zettl as ISDOC.",
      },
    ],
    faq: [
      {
        id: "nahrazuje",
        question: {
          cs: "Nahrazuje Zettl POHODU?",
          en: "Does Zettl replace POHODA?",
        },
        answer: {
          cs: "Ne. Zettl nevede účetnictví. Sbírá doklady, páruje je s platbami z Fio a předá je do POHODY, kde se účtuje.",
          en: "No. Zettl does no bookkeeping. It collects documents, matches them with Fio payments and hands them to POHODA, where the books are kept.",
        },
      },
      {
        id: "mserver",
        question: {
          cs: "Můžu doklady poslat do POHODY přímo, bez souboru?",
          en: "Can I send documents to POHODA directly, without a file?",
        },
        answer: {
          cs: "Ano, přes POHODA mServer, pokud je dostupný z internetu přes HTTPS. Opakované odeslání přeskočí, co už v POHODĚ je. Když mServer z internetu dostupný není, což je u POHODY na počítači v kanceláři obvyklé, stáhnete měsíc jako ZIP a naimportujete ho v POHODĚ.",
          en: "Yes, through POHODA mServer, if it is reachable from the internet over HTTPS. A repeated send skips what POHODA already has. If mServer isn't reachable from the internet, which is usual for POHODA on an office PC, download the month as a ZIP and import it in POHODA.",
        },
      },
      {
        id: "dph",
        question: {
          cs: "Přenese se do POHODY i rozpis DPH?",
          en: "Is the VAT breakdown carried into POHODA?",
        },
        answer: {
          cs: "Zatím ne. Zettl u přijatých dokladů rozpis DPH neukládá, takže každý doklad přijde celkovou částkou a sazby rozdělí účetní v POHODĚ.",
          en: "Not yet. Zettl stores no VAT breakdown for received documents, so each one arrives as its total and the accountant splits the rates in POHODA.",
        },
      },
      {
        id: "parovani",
        question: {
          cs: "Spárují se platby s doklady i v POHODĚ?",
          en: "Are payments paired with documents in POHODA too?",
        },
        answer: {
          cs: "Platba spárovaná v Zettlu s jedním dokladem nese jeho párový symbol, takže ji automatická likvidace v POHODĚ přiřadí. Platba spárovaná s několika doklady párový symbol nemá; čísla dokladů najdete v její interní poznámce.",
          en: "A payment matched in Zettl with one document carries that document's pairing symbol, so POHODA's automatic settlement assigns it. A payment matched with several documents has no pairing symbol; the document numbers are in its internal note.",
        },
      },
      {
        id: "cizi-mena",
        question: {
          cs: "Co doklady v cizí měně?",
          en: "What about documents in a foreign currency?",
        },
        answer: {
          cs: "Přijdou v cizí měně s kurzem ČNB ke dni vystavení dokladu.",
          en: "They arrive in the foreign currency with the ČNB rate for the document's issue date.",
        },
      },
    ],
    related: ["guideAccountants", "toolGpcPohoda", "guideAccountant", "guideIsdoc"],
  },
  guidePohodaHowTo: {
    key: "guidePohodaHowTo",
    seo: {
      title: {
        cs: "Návod: doklady ze Zettlu do POHODY (XML import i mServer) — Zettl",
        en: "Guide: documents from Zettl into POHODA (XML import and mServer) — Zettl",
      },
      description: {
        cs: "Krok za krokem se snímky: export měsíce do ZIPu a XML import v POHODĚ, nebo přímé odeslání přes POHODA mServer. Nastavení, práva, řešení potíží.",
        en: "Step by step with screenshots: export a month as a ZIP and import the XML in POHODA, or send it directly through POHODA mServer. Setup, rights, troubleshooting.",
      },
    },
    name: { cs: "Návod: Zettl → POHODA", en: "Guide: Zettl → POHODA" },
    eyebrow: { cs: "Návod krok za krokem", en: "Step-by-step guide" },
    h1: {
      cs: "Jak dostat doklady a platby ze Zettlu do POHODY",
      en: "How to get documents and payments from Zettl into POHODA",
    },
    lead: {
      cs: "Dvě cesty: stáhnete ZIP s XML a v POHODĚ ho naimportujete průvodcem XML import/export, nebo Zettl pošle měsíc rovnou do POHODY přes POHODA mServer. Přenesou se přijaté doklady a bankovní pohyby za měsíc; vydané faktury odcházejí jako ISDOC.",
      en: "Two ways: download a ZIP of XML and import it in POHODA with the XML import/export wizard, or let Zettl send the month straight into POHODA through POHODA mServer. A month's received documents and bank movements go across; issued invoices leave as ISDOC.",
    },
    steps: [
      {
        title: { cs: "Nastavení → Účetnictví", en: "Settings → Accounting" },
        text: {
          cs: "Export, napojení na mServer i stručný návod najdete v Zettlu na jednom místě: Nastavení → Účetnictví.",
          en: "The export, the mServer connection and a short guide are in one place in Zettl: Settings → Accounting.",
        },
      },
      {
        title: { cs: "Cesta 1: ZIP a XML import", en: "Way 1: ZIP and XML import" },
        text: {
          cs: "Vyberete měsíc, stáhnete ZIP a v POHODĚ ho naimportujete přes Soubor / Datová komunikace / XML import/export.",
          en: "Pick the month, download the ZIP and import it in POHODA through Soubor / Datová komunikace / XML import/export.",
        },
      },
      {
        title: { cs: "Cesta 2: přímo přes mServer", en: "Way 2: directly via mServer" },
        text: {
          cs: "Je-li POHODA mServer dostupný z internetu přes HTTPS, zadáte jeho adresu, uživatele a heslo a měsíc pošlete jedním tlačítkem.",
          en: "If POHODA mServer is reachable from the internet over HTTPS, enter its address, user and password and send the month with one button.",
        },
      },
      {
        title: { cs: "Opakovaně bez zdvojení", en: "Repeat without duplicates" },
        text: {
          cs: "Doklady mají stálá čísla: co už POHODA má, podruhé nepřijme, a přes mServer to Zettl ani znovu neposílá.",
          en: "Documents keep fixed numbers: POHODA won't accept what it already has, and over mServer Zettl doesn't even send it again.",
        },
      },
    ],
    sources: {
      checkedOn: "2026-11-03",
      subject: "POHODA",
      links: [
        {
          label: "Příručka POHODA: XML import/export",
          url: "https://www.stormware.cz/prirucka-pohoda-online/Datova_komunikace/XML_import-export/",
        },
        {
          label: "XML log",
          url: "https://www.stormware.cz/prirucka-pohoda-online/Datova_komunikace/XML_log/",
        },
        {
          label: "Banka (automatická likvidace)",
          url: "https://www.stormware.cz/prirucka-pohoda-online/Finance/Banka/",
        },
        {
          label: "Přijaté faktury",
          url: "https://www.stormware.cz/prirucka-pohoda-online/Fakturace/Prijate_faktury/",
        },
        {
          label: "Přístupová práva",
          url: "https://www.stormware.cz/prirucka-pohoda-online/Nastaveni/Pristupova_prava/",
        },
        { label: "POHODA mServer", url: "https://www.stormware.cz/pohoda/xml/mserver/" },
        {
          label: "Nastavení mServeru",
          url: "https://www.stormware.cz/pohoda/xml/mserver/nastaveni/",
        },
        {
          label: "Spuštění mServeru",
          url: "https://www.stormware.cz/pohoda/xml/mserver/spusteni/",
        },
        {
          label: "mServer pro vývojáře",
          url: "https://www.stormware.cz/pohoda/xml/mserver/provyvojare/",
        },
        {
          label: "XML schéma faktur (invoice.xsd)",
          url: "https://www.stormware.cz/schema/version_2/invoice.xsd",
        },
      ],
    },
    notes: [
      {
        cs: "Názvy nabídek a postupy v POHODĚ přebíráme z veřejné dokumentace Stormware; podle vaší verze POHODY se mohou lišit. Snímky obrazovky jsou jen ze Zettlu.",
        en: "POHODA's menu names and procedures come from Stormware's public documentation and may differ by your POHODA version. The screenshots show Zettl only.",
      },
      {
        cs: "Starší stránky dokumentace Stormware uvádějí právo na XML komunikaci ve větvi Práva / Soubor / Databáze, novější ve větvi Práva / Soubor / Datová komunikace.",
        en: "Older Stormware documentation pages put the XML communication right under Rights / File / Database, newer ones under Rights / File / Data communication.",
      },
      {
        cs: "Zettl u přijatých dokladů neukládá rozpis DPH, IČO a DIČ dodavatele ani DUZP: doklad přijde celou částkou bez sazby DPH a rozúčtuje ho účetní.",
        en: "For received documents Zettl stores no VAT breakdown, supplier IČO/DIČ or taxable supply date: a document arrives as its total without a VAT rate, for the accountant to split.",
      },
      {
        cs: "Bankovní pohyby importujte buď ze Zettlu, nebo z výpisu přímo do POHODY – ne obojí, jinak tam budou dvakrát.",
        en: "Import bank movements either from Zettl or from a statement straight into POHODA – not both, or they will be there twice.",
      },
    ],
    faq: [
      {
        id: "znovu",
        question: {
          cs: "Co se stane, když měsíc naimportuji nebo pošlu znovu?",
          en: "What happens if I import or send a month again?",
        },
        answer: {
          cs: "Každý doklad má v POHODĚ stálé číslo odvozené ze Zettlu, takže doklad, který už v POHODĚ je, POHODA podruhé nepřijme a nové doklady přidá. Bankovní pohyby takové číslo nemají, proto banka.xml za stejný měsíc importujte jen jednou. Přes mServer si Zettl pamatuje, co POHODA už má, a pošle jen nové doklady, pohyby a ty, které skončily chybou.",
          en: "Each document gets a fixed number in POHODA derived from Zettl, so POHODA refuses a document it already has and adds the new ones. Bank movements have no such number, so import banka.xml for a month only once. Over mServer, Zettl remembers what POHODA already has and sends only new documents and movements and the ones that failed.",
        },
      },
      {
        id: "co-ne",
        question: {
          cs: "Co se do POHODY nepřenese?",
          en: "What does not go into POHODA?",
        },
        answer: {
          cs: "Vydané faktury (ty odcházejí jako ISDOC), rozpis DPH, IČO a DIČ dodavatele, DUZP a splatnost přijatých dokladů a předkontace. Přes mServer se nepřenášejí ani soubory dokladů; ty jsou v ZIPu.",
          en: "Issued invoices (they leave as ISDOC), the VAT breakdown, supplier IČO and DIČ, taxable supply date and due date of received documents, and posting rules. Over mServer the document files don't go across either; they are in the ZIP.",
        },
      },
      {
        id: "agendy",
        question: {
          cs: "Do kterých agend v POHODĚ doklady přijdou?",
          en: "Which POHODA agendas do the documents land in?",
        },
        answer: {
          cs: "Přijaté doklady do agendy Přijaté faktury (nabídka Fakturace), bankovní pohyby do agendy Banka (nabídka Účetnictví). Výsledek importu ukáže agenda XML log.",
          en: "Received documents land in the Přijaté faktury agenda (Fakturace menu), bank movements in the Banka agenda (Účetnictví menu). The XML log agenda shows the import result.",
        },
      },
      {
        id: "pristupy",
        question: {
          cs: "Kdo potřebuje jaký přístup?",
          en: "Who needs which access?",
        },
        answer: {
          cs: "V Zettlu může export stáhnout a měsíc odeslat každý člen firmy, napojení na mServer nastavuje vlastník nebo administrátor. V POHODĚ potřebuje uživatel právo na XML komunikaci, přístup k účetní jednotce a zápis do agend Přijaté faktury a Banka; pro mServer navíc větev Administrátorské funkce / POHODA mServer.",
          en: "In Zettl any member of the company can download the export and send the month; the owner or an admin sets up the mServer connection. In POHODA the user needs the XML communication right, access to the accounting unit and write access to the Přijaté faktury and Banka agendas; for mServer also the Administrator functions / POHODA mServer branch.",
        },
      },
      {
        id: "mserver-nutny",
        question: {
          cs: "Potřebuji mServer?",
          en: "Do I need mServer?",
        },
        answer: {
          cs: "Ne. ZIP s XML funguje bez mServeru a bez jakéhokoli napojení. mServer jen ušetří stahování a ruční import, ale musí být dostupný z internetu přes HTTPS.",
          en: "No. The ZIP of XML works without mServer and without any connection. mServer only saves the download and the manual import, but it must be reachable from the internet over HTTPS.",
        },
      },
      {
        id: "mserver-bezi",
        question: {
          cs: "Musí mServer běžet pořád?",
          en: "Must mServer run all the time?",
        },
        answer: {
          cs: "Ne. Stačí, když běží ve chvíli, kdy z Zettlu odesíláte. Stormware nepřetržitý běh mServeru nedoporučuje.",
          en: "No. It only has to run while you send from Zettl. Stormware does not recommend running mServer non-stop.",
        },
      },
    ],
    tool: "pohodaHowTo",
    related: ["guidePohoda", "toolGpcPohoda", "guideAccountant", "guideAccountants"],
  },

  // supabase/functions/run-ai-matching (rules, then AI; validate.ts), _shared/sum-match.ts,
  // daily-sync, src/lib/unmatch.ts (rejected_matches), src/lib/manualMatch.ts, ReceiptSetPicker.
  guideMatching: {
    key: "guideMatching",
    updated: "2026-11-02",
    seo: {
      title: {
        cs: "Párování plateb s doklady a účtenkami automaticky — Zettl",
        en: "Match bank payments with receipts and invoices automatically — Zettl",
      },
      description: {
        cs: "Zettl ke každé platbě z Fio banky hledá účtenku nebo fakturu podle částky, data a dodavatele. Co se nespáruje, vidíte hned.",
        en: "Zettl looks for the receipt or invoice for each Fio banka payment by amount, date and vendor. Whatever is left unmatched, you see at once.",
      },
    },
    name: { cs: "Párování plateb s doklady", en: "Matching payments with documents" },
    eyebrow: { cs: "Párování", en: "Matching" },
    h1: {
      cs: "Párování plateb s doklady a účtenkami, automaticky",
      en: "Match payments with receipts and invoices, automatically",
    },
    lead: {
      cs: "Zettl každé ráno stáhne platby z vašeho účtu u Fio banky a ke každé hledá účtenku nebo fakturu podle částky, data a dodavatele. Spárované platby uvidíte zeleně, ty bez dokladu zůstanou viditelně otevřené.",
      en: "Every morning Zettl downloads the payments from your Fio banka account and looks for each one's receipt or invoice by amount, date and vendor. Matched payments show green; the ones without a document stay visibly open.",
    },
    steps: [
      {
        title: { cs: "Platby přijdou z Fio", en: "Payments arrive from Fio" },
        text: {
          cs: "Přes Fio API Zettl každý den stáhne nové pohyby na účtu. Starší období doplníte výpisem GPC/ABO.",
          en: "Through the Fio API Zettl downloads new account movements every day. Older periods come in as a GPC/ABO statement.",
        },
      },
      {
        title: { cs: "Doklady přijdou e-mailem", en: "Documents arrive by e-mail" },
        text: {
          cs: "Účtenky a faktury přepošlete na adresu vaší firmy v Zettlu nebo je nahrajete. AI z nich přečte dodavatele, datum a částku.",
          en: "Forward receipts and invoices to your company's Zettl address or upload them. AI reads the vendor, date and amount.",
        },
      },
      {
        title: { cs: "Nejdřív přesná pravidla", en: "Exact rules first" },
        text: {
          cs: "Sedí-li částka a měna na haléř přesně a hodí se jen jeden doklad, Zettl spáruje rovnou. Stejně tak když platbu přesně pokryje součet několika dokladů.",
          en: "When amount and currency agree to the cent and only one document fits, Zettl matches at once. The same goes for a payment covered exactly by the sum of several documents.",
        },
      },
      {
        title: { cs: "Pak AI pro zbytek", en: "Then AI for the rest" },
        text: {
          cs: "Zbylé platby AI porovná s doklady podle dodavatele, data, částky a popisu platby. U plateb ve stejné měně se uloží jen párování, kde částka sedí do 5 % a datum do 7 dní.",
          en: "AI compares the remaining payments with documents by vendor, date, amount and payment description. For payments in the same currency, a match is saved only if the amount agrees within 5% and the date within 7 days.",
        },
      },
      {
        title: { cs: "Vy zkontrolujete", en: "You review" },
        text: {
          cs: "Každé párování má skóre shody. Špatné zrušíte jedním klikem a Zettl tu dvojici už znovu nenavrhne.",
          en: "Every match carries a confidence score. Undo a wrong one with one click and Zettl never suggests that pair again.",
        },
      },
      {
        title: { cs: "Zbytek ručně", en: "The rest by hand" },
        text: {
          cs: "Doklad přetáhnete na platbu, nebo k platbě vyberete víc dokladů najednou. Zettl ukazuje, jestli součet sedí.",
          en: "Drag a document onto a payment, or pick several documents for one payment. Zettl shows whether the sum adds up.",
        },
      },
    ],
    notes: [
      {
        cs: "Párování běží každé ráno po stažení plateb a na tlačítko pro vybraný měsíc. Hned po nahrání dokladu se nespouští.",
        en: "Matching runs every morning after the payments are downloaded, and on a button for the selected month. It does not start the moment a document is uploaded.",
      },
      {
        cs: "Zettl párování uloží rovnou, bez potvrzování. Proto má každé skóre a jde jedním klikem zrušit.",
        en: "Zettl saves matches straight away, with no confirmation step. That is why each has a score and can be undone with one click.",
      },
      {
        cs: "Příchozí platbu Zettl páruje i s vydanou fakturou, podle variabilního symbolu a částky.",
        en: "Zettl also matches incoming payments with issued invoices, by variable symbol and amount.",
      },
      {
        cs: "Funguje jen s platbami z Fio banky (API nebo výpis GPC/ABO).",
        en: "It works only with payments from Fio banka (API or GPC/ABO statement).",
      },
    ],
    faq: [
      {
        id: "jak-paruje",
        question: {
          cs: "Podle čeho Zettl páruje platby s doklady?",
          en: "How does Zettl decide which document belongs to a payment?",
        },
        answer: {
          cs: "Nejdřív přesnými pravidly: stejná částka a měna, nebo přesný součet několika dokladů ze stejných dnů. Zbytek posoudí AI podle dodavatele, data, částky, variabilního symbolu a popisu platby. Ve stejné měně se párování z AI uloží, jen když částka sedí do 5 % a datum do 7 dní; párování napříč měnami musí mít vysoké skóre shody.",
          en: "Exact rules first: the same amount and currency, or the exact sum of several documents from around the same days. AI judges the rest by vendor, date, amount, variable symbol and payment description. In the same currency an AI match is saved only if the amount agrees within 5% and the date within 7 days; a match across currencies needs a high confidence score.",
        },
      },
      {
        id: "chyba",
        question: {
          cs: "Co když Zettl spáruje špatně?",
          en: "What if Zettl matches the wrong document?",
        },
        answer: {
          cs: "Párování jedním klikem zrušíte. Zettl si zrušenou dvojici zapamatuje a automaticky ji už znovu nenavrhne. Správný doklad pak přiřadíte ručně.",
          en: "Undo the match with one click. Zettl remembers the rejected pair and never proposes it automatically again. Then assign the right document by hand.",
        },
      },
      {
        id: "kdy",
        question: {
          cs: "Kdy párování probíhá?",
          en: "When does matching run?",
        },
        answer: {
          cs: "Každé ráno po stažení plateb z Fio, pro platby za posledních 60 dní. Kdykoli ho také spustíte tlačítkem pro vybraný měsíc.",
          en: "Every morning after the Fio download, for payments from the last 60 days. You can also start it any time with a button for the selected month.",
        },
      },
      {
        id: "chybejici",
        question: {
          cs: "Jak zjistím, ke kterým platbám chybí doklad?",
          en: "How do I see which payments still lack a document?",
        },
        answer: {
          cs: "V přehledu měsíce jsou nespárované platby vidět zvlášť. Víte tak, co ještě dohledat, než podklady pošlete účetní.",
          en: "The month view lists unmatched payments separately, so you know what is still missing before you send everything to your accountant.",
        },
      },
    ],
    related: ["guideMonthClose", "guideEmailInbox", "guideSplitPayment", "guideAccountant"],
  },

  // fetch-fio-transactions (periods endpoint), daily-sync (06:00 UTC, 45 days),
  // BankAccountsSection (manual and historical sync), _shared/encryption.ts.
  guideFioApi: {
    key: "guideFioApi",
    updated: "2026-11-02",
    seo: {
      title: {
        cs: "Fio API a účetnictví: automatické stahování plateb — Zettl",
        en: "Fio banka API for bookkeeping: automatic payment download — Zettl",
      },
      description: {
        cs: "Jak napojit Fio banku přes API token: Zettl každý den sám stáhne pohyby na účtu a spáruje je s doklady. Jen pro čtení, bez práva zadávat platby.",
        en: "How to connect Fio banka with an API token: Zettl downloads your account movements every day and matches them with documents. Read-only, no right to send payments.",
      },
    },
    name: { cs: "Fio API do účetnictví", en: "Fio API for bookkeeping" },
    eyebrow: { cs: "Fio banka", en: "Fio banka" },
    h1: {
      cs: "Platby z Fio banky automaticky, přes Fio API",
      en: "Fio banka payments, automatically, through the Fio API",
    },
    lead: {
      cs: "Ve Fio internetbankingu vytvoříte API token pro sledování účtu a vložíte ho do Zettlu. Zettl pak každé ráno sám stáhne nové pohyby a spáruje je s doklady. Platby zadávat neumí.",
      en: "Create an account-monitoring API token in Fio internet banking and paste it into Zettl. From then on Zettl downloads new movements every morning and matches them with documents. It cannot send payments.",
    },
    steps: [
      {
        title: { cs: "Vytvořte token ve Fio", en: "Create a token at Fio" },
        text: {
          cs: "Internetbanking Fio → Nastavení → API → nový token s oprávněním „Sledování účtu“. Fio ho potvrdí SMS nebo push zprávou.",
          en: "Fio internet banking → Settings → API → new token with the “Account monitoring” permission. Fio confirms it by SMS or push notification.",
        },
      },
      {
        title: { cs: "Vložte ho do Zettlu", en: "Paste it into Zettl" },
        text: {
          cs: "V Nastavení → Banky a integrace přidáte účet a token. Zettl ho uloží šifrovaný.",
          en: "Under Settings → Banks & integrations, add the account and the token. Zettl stores it encrypted.",
        },
      },
      {
        title: { cs: "Každé ráno nové platby", en: "New payments every morning" },
        text: {
          cs: "Zettl jednou denně stáhne pohyby za posledních 45 dní a doplní, co ještě nemá. Synchronizaci můžete spustit i ručně.",
          en: "Once a day Zettl downloads the last 45 days of movements and adds what it does not have yet. You can also start a sync by hand.",
        },
      },
      {
        title: { cs: "A rovnou párování", en: "And matching straight after" },
        text: {
          cs: "Po stažení Zettl k platbám hledá účtenky a faktury a příchozí platby páruje s vydanými fakturami podle variabilního symbolu.",
          en: "After the download Zettl looks for receipts and invoices for the payments and matches incoming payments with issued invoices by variable symbol.",
        },
      },
    ],
    notes: [
      {
        cs: "Fio přes API bez další autorizace vydá jen posledních 90 dní. Starší pohyby nahrajete jako výpis GPC/ABO.",
        en: "Without extra authorization Fio's API only releases the last 90 days. Upload older movements as a GPC/ABO statement.",
      },
      {
        cs: "Token u Fio platí pro jeden účet: korunový a eurový účet přidáte zvlášť.",
        en: "A Fio token covers one account: add your CZK and EUR accounts separately.",
      },
      {
        cs: `Tarif ${start.name} připojí ${start.limits.fioAccounts} účty u Fio, tarif ${firma.name} ${firma.limits.fioAccounts}.`,
        en: `The ${start.name} plan connects ${start.limits.fioAccounts} Fio accounts, ${firma.name} ${firma.limits.fioAccounts}.`,
      },
      {
        cs: "Zettl volá jen rozhraní Fio API, která čtou pohyby a výpisy. Token je uložený šifrovaně (AES-256-GCM).",
        en: "Zettl only calls the parts of the Fio API that read movements and statements. The token is stored encrypted (AES-256-GCM).",
      },
      {
        cs: "Zettl podporuje jen Fio banku. Jiné banky napojit nelze.",
        en: "Zettl supports Fio banka only. Other banks cannot be connected.",
      },
    ],
    faq: [
      {
        id: "prava-tokenu",
        question: {
          cs: "Jaká oprávnění má mít Fio API token?",
          en: "Which permission does the Fio API token need?",
        },
        answer: {
          cs: "Stačí „Sledování účtu“. Právo zadávat platby Zettl nepotřebuje a nepoužije. Pokud chcete ze Zettlu stahovat i PDF výpisy z Fio, zapněte tokenu ve Fio také stahování PDF výpisů.",
          en: "“Account monitoring” is enough. Zettl neither needs nor uses the right to send payments. If you also want to download Fio's PDF statements from Zettl, enable PDF statement download for the token at Fio too.",
        },
      },
      {
        id: "platnost",
        question: {
          cs: "Jak dlouho token platí?",
          en: "How long is a token valid?",
        },
        answer: {
          cs: "Platnost tokenu nastavíte ve Fio při jeho vytvoření. Když zapnete prodlužování platnosti při přihlášení, každé přihlášení do internetového bankovnictví ji prodlouží.",
          en: "You set the validity at Fio when you create the token. If you turn on extending the validity at login, every login to internet banking extends it.",
        },
      },
      {
        id: "jak-casto",
        question: {
          cs: "Jak často Zettl platby stahuje?",
          en: "How often does Zettl download payments?",
        },
        answer: {
          cs: "Automaticky jednou denně, ráno. Kdykoli mezi tím můžete stažení spustit ručně v přehledu nebo u účtu v nastavení.",
          en: "Automatically once a day, in the morning. You can start a download by hand any time in between, on the dashboard or at the account in settings.",
        },
      },
      {
        id: "bezpecnost",
        question: {
          cs: "Může Zettl z účtu odeslat peníze?",
          en: "Can Zettl send money from my account?",
        },
        answer: {
          cs: "Ne. Zettl nemá žádnou funkci pro zadání platby a volá jen rozhraní, která čtou pohyby a výpisy. Token ve Fio kdykoli zrušíte.",
          en: "No. Zettl has no function for sending payments and only calls the interfaces that read movements and statements. You can revoke the token at Fio any time.",
        },
      },
    ],
    related: ["guideFioToken", "guideGpcImport", "guideMatching", "guideAccountant"],
  },

  // Every fact from Fio's own "FIO API Bankovnictví" documentation, version 1.9 of
  // 16 Oct 2025 (§1, §2 "Získání tokenu" / "Vlastnosti tokenu", §3 on data older than
  // 90 days, §7 security, §8 "Známé chybové stavy") and fio.cz/bankovni-sluzby/
  // api-bankovnictvi ("API je zdarma"). Zettl's side: src/lib/fio/tokenHealth.ts (#177).
  guideFioToken: {
    key: "guideFioToken",
    updated: "2026-11-04",
    seo: {
      title: {
        cs: "Fio API token: jak ho vytvořit a prodloužit (180 dní) — Zettl",
        en: "Fio API token: how to create and renew it (180 days) — Zettl",
      },
      description: {
        cs: "Token pro Fio API vytvoříte v internetbankingu v Nastavení → API. Platí nejvýš 180 dní, s automatickým prodlužováním se při přihlášení obnoví. Postup a chyby 409 a 500.",
        en: "Create a Fio API token in internet banking under Settings → API. It is valid for at most 180 days and auto-renewal extends it at each login. Steps, and errors 409 and 500.",
      },
    },
    name: { cs: "Fio API token krok za krokem", en: "Fio API token, step by step" },
    eyebrow: { cs: "Fio banka", en: "Fio banka" },
    h1: {
      cs: "Jak vytvořit API token ve Fio bance",
      en: "How to create an API token at Fio banka",
    },
    lead: {
      cs: "V internetovém bankovnictví Fio klikněte vpravo nahoře na Nastavení, otevřete záložku API a založte token. Pro účetní program stačí právo „Sledování účtu“. Žádost potvrdíte SMS nebo push notifikací a za 5 minut token funguje. Platí nejvýš 180 dní; s automatickým prodlužováním se obnoví při každém přihlášení.",
      en: "In Fio internet banking, click Settings at the top right, open the API tab and create a token. For bookkeeping software the “Account monitoring” permission is enough. You confirm the request by SMS or push notification, and the token works 5 minutes later. It is valid for at most 180 days; with automatic renewal it is extended at every login.",
    },
    steps: [
      {
        title: { cs: "Přihlaste se do internetbankingu", en: "Sign in to internet banking" },
        text: {
          cs: "Token zakládá majitel účtu nebo osoba s oprávněním k účtu, ve svém internetovém bankovnictví Fio.",
          en: "The account owner, or a person with rights to the account, creates the token in their own Fio internet banking.",
        },
      },
      {
        title: { cs: "Nastavení → záložka API", en: "Settings → the API tab" },
        text: {
          cs: "Vpravo nahoře klikněte na „Nastavení“ a na obrazovce Nastavení otevřete záložku „API“. Tam se tokeny zakládají i ruší.",
          en: "Click “Nastavení” (Settings) at the top right and open the “API” tab on the Settings screen. Tokens are created and revoked there.",
        },
      },
      {
        title: { cs: "Zvolte práva a platnost", en: "Choose the permission and validity" },
        text: {
          cs: "Pro stahování plateb zvolte „Sledování účtu“. Nastavte platnost (nejvýš 180 dní) a zapněte automatické prodlužování, ať token nevyprší.",
          en: "To download payments, choose “Sledování účtu” (account monitoring). Set the validity (180 days at most) and turn on automatic renewal so the token doesn't expire.",
        },
      },
      {
        title: { cs: "Potvrďte SMS nebo push zprávou", en: "Confirm by SMS or push" },
        text: {
          cs: "Založení tokenu je potřeba silně autorizovat. Když účet podepisuje víc osob, musí token podepsat všechny. Pak se token objeví v přehledu.",
          en: "Creating a token needs strong authorization. If several people sign for the account, all of them must sign the token. The token then appears in the list.",
        },
      },
      {
        title: { cs: "Za 5 minut ho použijte", en: "Use it after 5 minutes" },
        text: {
          cs: "Token je 64znakový řetězec. V API začne fungovat 5 minut po autorizaci; pak ho vložte do účetního programu, třeba do Zettlu.",
          en: "The token is a 64-character string. It starts working in the API 5 minutes after authorization; then paste it into your bookkeeping software, Zettl for example.",
        },
      },
    ],
    notes: [
      {
        cs: "Jeden token platí jen pro jeden účet. Pro korunový a eurový účet založte tokeny dva.",
        en: "A token covers one account only. Create two tokens for a CZK and a EUR account.",
      },
      {
        cs: "Token bez platnosti nebo s platností delší než 180 dní Fio založit nedovolí. S automatickým prodlužováním se platnost při každém přihlášení do Internetbankingu nebo Smartbankingu nastaví znovu na 180 dní od toho dne.",
        en: "Fio won't create a token without a validity period or with one longer than 180 days. With automatic renewal, every login to Internetbanking or Smartbanking resets the validity to 180 days from that day.",
      },
      {
        cs: "Bez další autorizace vydá API pohyby jen za posledních 90 dní. Starší data odemknete ve stejné záložce API ikonou zámku u tokenu; po autorizaci jsou přístupná 10 minut.",
        en: "Without further authorization the API only releases the last 90 days. Unlock older data on the same API tab with the lock icon next to the token; after authorization they are available for 10 minutes.",
      },
      {
        cs: "Mezi dvěma dotazy se stejným tokenem musí uplynout aspoň 30 sekund. Jinak Fio odpoví chybou 409 Conflict.",
        en: "At least 30 seconds must pass between two calls with the same token. Otherwise Fio answers 409 Conflict.",
      },
      {
        cs: "Chyba 500 Internal Server Error u Fio API znamená neexistující nebo neaktivní token, typicky vypršelý. Založte nový a vyměňte ho v programu.",
        en: "A 500 Internal Server Error from the Fio API means the token doesn't exist or is inactive, typically expired. Create a new one and replace it in your software.",
      },
      {
        cs: "Token je citlivý údaj: kdo ho má, vidí pohyby na účtu. Právo zadávat platby dávejte jen programu, který platby opravdu posílá.",
        en: "A token is sensitive: whoever has it can see the account's movements. Give the right to send payments only to software that really sends payments.",
      },
    ],
    sources: {
      checkedOn: "2026-11-04",
      intro: {
        cs: "Údaje o Fio API jsme ověřili dne {date} ve veřejné dokumentaci Fio banky: ",
        en: "We checked the facts about the Fio API on {date} in Fio banka's public documentation: ",
      },
      links: [
        {
          label: "FIO API Bankovnictví, dokumentace verze 1.9 (16. 10. 2025)",
          url: "https://www.fio.cz/docs/cz/API_Bankovnictvi.pdf",
        },
        {
          label: "API Bankovnictví na fio.cz",
          url: "https://www.fio.cz/bankovni-sluzby/api-bankovnictvi",
        },
      ],
    },
    faq: [
      {
        id: "kde-token",
        question: {
          cs: "Kde ve Fio internetbankingu najdu API token?",
          en: "Where is the API token in Fio internet banking?",
        },
        answer: {
          cs: "Vpravo nahoře pod tlačítkem „Nastavení“, na záložce „API“. Tam tokeny zakládáte, vidíte jejich přehled a rušíte je.",
          en: "Under the “Nastavení” (Settings) button at the top right, on the “API” tab. That's where you create, list and revoke tokens.",
        },
      },
      {
        id: "jaka-prava",
        question: {
          cs: "Jaká práva má mít token pro účetní program?",
          en: "Which permission should a token for bookkeeping software have?",
        },
        answer: {
          cs: "„Sledování účtu“: token jen stahuje data z banky. Druhá možnost, „Sledování účtu a zadávání platebních a inkasních příkazů“, umí platby i posílat a účetní program ji ke stahování plateb nepotřebuje.",
          en: "“Sledování účtu” (account monitoring): the token only downloads data from the bank. The other option, account monitoring plus payment and direct debit orders, can also send payments, and bookkeeping software doesn't need it to download payments.",
        },
      },
      {
        id: "platnost",
        question: {
          cs: "Jak dlouho Fio API token platí?",
          en: "How long is a Fio API token valid?",
        },
        answer: {
          cs: "Nejvýš 180 dní, platnost volíte při založení. Když zapnete automatické prodlužování, každé přihlášení do Internetbankingu nebo Smartbankingu ji prodlouží na 180 dní od toho dne.",
          en: "At most 180 days; you choose the validity when you create it. With automatic renewal on, every login to Internetbanking or Smartbanking extends it to 180 days from that day.",
        },
      },
      {
        id: "nefunguje-hned",
        question: {
          cs: "Proč nový token hned nefunguje?",
          en: "Why doesn't a new token work straight away?",
        },
        answer: {
          cs: "Fio ho pustí do API až 5 minut po úspěšné autorizaci. Pokud účet podepisuje víc osob, musí token nejdřív podepsat všechny.",
          en: "Fio lets it into the API only 5 minutes after successful authorization. If several people sign for the account, all of them must sign the token first.",
        },
      },
      {
        id: "chyba-500",
        question: {
          cs: "Co znamená chyba 500 nebo 409 z Fio API?",
          en: "What do errors 500 and 409 from the Fio API mean?",
        },
        answer: {
          cs: "500 znamená neexistující nebo neaktivní token, nejčastěji vypršelý: založte nový. 409 znamená, že od minulého dotazu se stejným tokenem neuplynulo 30 sekund: stačí chvíli počkat.",
          en: "500 means the token doesn't exist or is inactive, most often expired: create a new one. 409 means less than 30 seconds have passed since the last call with the same token: just wait a moment.",
        },
      },
      {
        id: "starsi-90",
        question: {
          cs: "Jak přes API stáhnu pohyby starší než 90 dní?",
          en: "How do I download movements older than 90 days through the API?",
        },
        answer: {
          cs: "V záložce API klikněte u tokenu na ikonu zámku a pokyn autorizujte. Celá historie je pak přístupná 10 minut. Do Zettlu můžete starší pohyby nahrát i jako výpis GPC.",
          en: "On the API tab, click the lock icon next to the token and authorize the request. The full history is then available for 10 minutes. In Zettl you can also upload older movements as a GPC statement.",
        },
      },
      {
        id: "zettl-hlida",
        question: {
          cs: "Pozná Zettl, že token brzy vyprší?",
          en: "Does Zettl notice when a token is about to expire?",
        },
        answer: {
          cs: "Ano. Fio API datum vypršení neříká, proto Zettl počítá stáří tokenu od jeho uložení a po 165 dnech upozorní. Upozorní i tehdy, když Fio token déle než den nepřijímá. V obou případech to ukáže v aplikaci a pošle jeden e-mail vlastníkům a správcům firmy.",
          en: "Yes. Fio's API doesn't say when a token expires, so Zettl counts the token's age from when it was saved and warns after 165 days. It also warns when Fio has refused the token for over a day. Either way it shows in the app, with one e-mail to the company's owners and admins.",
        },
      },
    ],
    related: ["guideFioApi", "guideGpcImport", "toolGpcToCsv", "guideMatching"],
  },
  // src/lib/gpc/gpcParser.ts (074/075, windows-1250, balance check), gpcToTransactions.ts
  // (deterministic ids, CZK), GpcImportCard (.gpc/.abo/.txt, crossSourceGuard).
  guideGpcImport: {
    key: "guideGpcImport",
    updated: "2026-11-02",
    seo: {
      title: {
        cs: "Import výpisu GPC / ABO z Fio banky — Zettl",
        en: "Import a GPC / ABO statement from Fio banka — Zettl",
      },
      description: {
        cs: "Starší pohyby z Fio nahrajete jako výpis GPC (ABO). Zettl ho načte, zkontroluje zůstatky a platby, které už má z Fio API, nezdvojí.",
        en: "Upload older Fio movements as a GPC (ABO) statement. Zettl reads it, checks the balances, and never duplicates payments it already has from the Fio API.",
      },
    },
    name: { cs: "Import výpisu GPC/ABO", en: "GPC/ABO statement import" },
    eyebrow: { cs: "Výpisy", en: "Statements" },
    h1: {
      cs: "Import výpisu GPC / ABO z Fio banky",
      en: "Import a GPC / ABO statement from Fio banka",
    },
    lead: {
      cs: "GPC (ABO) je formát elektronického výpisu, který si stáhnete ve Fio internetbankingu. V Zettlu ho nahrajete pro období, které Fio API bez autorizace nevydá: platby přibudou bez duplicit a Zettl je spáruje s doklady.",
      en: "GPC (ABO) is the electronic statement format you download in Fio internet banking. In Zettl you upload it for periods the Fio API won't release without authorization: the payments are added without duplicates and Zettl matches them with documents.",
    },
    steps: [
      {
        title: { cs: "Stáhněte výpis ve Fio", en: "Download the statement at Fio" },
        text: {
          cs: "V internetbankingu Fio vyberete výpis za období a formát GPC (ABO).",
          en: "In Fio internet banking choose the statement for the period and the GPC (ABO) format.",
        },
      },
      {
        title: { cs: "Nahrajte ho do Zettlu", en: "Upload it to Zettl" },
        text: {
          cs: "Transakce → Import / Export → výpis GPC. Zettl přijme soubory .gpc, .abo i .txt.",
          en: "Transactions → Import / Export → GPC statement. Zettl accepts .gpc, .abo and .txt files.",
        },
      },
      {
        title: { cs: "Kontrola před uložením", en: "Checked before saving" },
        text: {
          cs: "Zettl ukáže náhled plateb a ověří, že počáteční zůstatek, obraty a konečný zůstatek do sebe zapadají.",
          en: "Zettl shows a preview of the payments and checks that opening balance, turnovers and closing balance agree.",
        },
      },
      {
        title: { cs: "Bez duplicit", en: "No duplicates" },
        text: {
          cs: "Stejný výpis nahraný dvakrát nic nezdvojí. Platby, které Zettl už stáhl přes Fio API, přeskočí.",
          en: "The same statement uploaded twice adds nothing twice. Payments Zettl already downloaded through the Fio API are skipped.",
        },
      },
    ],
    notes: [
      {
        cs: "Zettl čte záznamy 074 (hlavička výpisu) a 075 (pohyb), v kódování windows-1250. Jeden soubor může obsahovat víc výpisů.",
        en: "Zettl reads records 074 (statement header) and 075 (movement), encoded windows-1250. One file may hold several statements.",
      },
      {
        cs: "Doplňkové záznamy se zprávou pro příjemce (078, 079) Zettl přeskočí. Popis platby složí z protistrany, variabilního symbolu a čísla účtu.",
        en: "Zettl skips the extra message records (078, 079). It builds the payment description from the counterparty, variable symbol and account number.",
      },
      {
        cs: "GPC neobsahuje měnu: Zettl výpis načte jako korunový.",
        en: "GPC carries no currency: Zettl reads the statement as CZK.",
      },
      {
        cs: "Kromě GPC/ABO Zettl přijme i výpis CSV.",
        en: "Besides GPC/ABO, Zettl also accepts a CSV statement.",
      },
    ],
    faq: [
      {
        id: "proc-gpc",
        question: {
          cs: "Proč nahrávat GPC, když mám napojené Fio API?",
          en: "Why upload GPC if the Fio API is connected?",
        },
        answer: {
          cs: "Fio přes API bez další autorizace vydá jen posledních 90 dní. Starší pohyby, třeba za celý loňský rok, doplníte výpisem GPC (ABO).",
          en: "Without extra authorization Fio's API only releases the last 90 days. Older movements, say the whole of last year, come in as a GPC (ABO) statement.",
        },
      },
      {
        id: "duplicity",
        question: {
          cs: "Nezdvojí se platby, které už Zettl má z API?",
          en: "Won't payments Zettl already has from the API be duplicated?",
        },
        answer: {
          cs: "Ne. Každý řádek výpisu dostane stálý identifikátor, takže opakované nahrání nic nepřidá, a platby, které už v Zettlu jsou z Fio API, import přeskočí. Dvě skutečné platby se stejnou částkou ve stejný den zůstanou obě.",
          en: "No. Every statement line gets a stable identifier, so uploading again adds nothing, and payments already in Zettl from the Fio API are skipped. Two genuine payments of the same amount on the same day both stay.",
        },
      },
      {
        id: "jine-banky",
        question: {
          cs: "Můžu nahrát výpis GPC z jiné banky?",
          en: "Can I upload a GPC statement from another bank?",
        },
        answer: {
          cs: "Zettl je určený pro Fio banku a import GPC je tak také stavěný a testovaný. Napojení jiných bank Zettl nepodporuje.",
          en: "Zettl is built for Fio banka, and the GPC import is built and tested for it. Zettl does not support other banks.",
        },
      },
      {
        id: "data",
        question: {
          cs: "Které údaje z výpisu Zettl načte?",
          en: "Which details does Zettl read from the statement?",
        },
        answer: {
          cs: "Datum, částku, číslo účtu a kód banky protistrany, název protistrany a variabilní, konstantní a specifický symbol.",
          en: "Date, amount, the counterparty's account number and bank code, counterparty name, and the variable, constant and specific symbols.",
        },
      },
    ],
    related: ["toolGpcToCsv", "guideFioApi", "guideMatching"],
  },

  // src/components/guides/GpcConverter.tsx: parseGpc + gpcToCsv in the browser, Blob download,
  // no request. src/lib/gpc/gpcToCsv.ts (columns, dialects, csvCell).
  toolGpcToCsv: {
    key: "toolGpcToCsv",
    updated: "2026-11-03",
    seo: {
      title: {
        cs: "Převod výpisu GPC / ABO do CSV a Excelu zdarma, v prohlížeči — Zettl",
        en: "Convert a GPC / ABO statement to CSV or Excel, free and in your browser — Zettl",
      },
      description: {
        cs: "Převeďte výpis GPC / ABO z Fio banky do CSV nebo Excelu (XLSX). Zdarma, bez registrace; soubor se nikam neodesílá, převod proběhne ve vašem prohlížeči.",
        en: "Convert a GPC / ABO statement from Fio banka to CSV or Excel (XLSX). Free, no sign-up; the file is never sent anywhere, the conversion runs in your browser.",
      },
    },
    name: { cs: "Převod GPC / ABO do CSV a Excelu", en: "GPC / ABO to CSV and Excel" },
    eyebrow: { cs: "Nástroj zdarma", en: "Free tool" },
    h1: {
      cs: "Převod výpisu GPC / ABO do CSV a Excelu",
      en: "Convert a GPC / ABO statement to CSV or Excel",
    },
    lead: {
      cs: "Nahrajte výpis GPC (ABO) z Fio banky a stáhněte si ho jako CSV nebo jako sešit Excelu (XLSX). Zdarma a bez registrace. Soubor se nikam neodesílá: převod proběhne jen ve vašem prohlížeči.",
      en: "Load a GPC (ABO) statement from Fio banka and download it as a CSV or an Excel workbook (XLSX). Free, no sign-up. The file is never sent anywhere: the conversion runs only in your browser.",
    },
    freeTool: {
      summary: {
        cs: "Výpis GPC / ABO z Fio banky převede do CSV nebo Excelu (XLSX), s náhledem pohybů.",
        en: "Turns a GPC / ABO statement from Fio banka into a CSV or an Excel workbook (XLSX), with a preview of the movements.",
      },
      bridge: {
        title: { cs: "Výpisy převádíte každý měsíc?", en: "Converting statements every month?" },
        text: {
          cs: "Zettl stahuje platby z Fio přes API každé ráno sám a ke každé hledá účtenku nebo fakturu. Výpis GPC pak potřebujete jen pro období starší než 90 dní.",
          en: "Zettl downloads your Fio payments through the API every morning and looks for each one's receipt or invoice. You then need a GPC statement only for periods older than 90 days.",
        },
        guide: "guideFioApi",
      },
    },
    tool: "gpcToCsv",
    steps: [
      {
        title: { cs: "Stáhněte výpis ve Fio", en: "Download the statement at Fio" },
        text: {
          cs: "V internetbankingu Fio vyberete výpis a formát GPC (ABO).",
          en: "In Fio internet banking choose the statement and the GPC (ABO) format.",
        },
      },
      {
        title: { cs: "Vyberte soubor", en: "Pick the file" },
        text: {
          cs: "Přetáhněte ho výše nebo ho vyberte. Hned uvidíte náhled pohybů.",
          en: "Drop it above or choose it. You see a preview of the movements at once.",
        },
      },
      {
        title: { cs: "Stáhněte CSV nebo Excel", en: "Download the CSV or Excel file" },
        text: {
          cs: "Jeden řádek na pohyb: účet, datum, částka, protiúčet, kód banky, protistrana, symboly a číslo dokladu. V Excelu (XLSX) je částka číslo, které rovnou sečtete.",
          en: "One row per movement: account, date, amount, counter account, bank code, counterparty, symbols and document number. In Excel (XLSX) the amount is a number you can add up straight away.",
        },
      },
      {
        title: { cs: "Nebo rovnou do Zettlu", en: "Or straight into Zettl" },
        text: {
          cs: "V Zettlu výpis GPC nahrajete přímo a platby se spárují s účtenkami a fakturami.",
          en: "In Zettl you upload the GPC statement directly and the payments get matched with receipts and invoices.",
        },
      },
    ],
    notes: [
      {
        cs: "Soubor se neodesílá na žádný server a nic se neukládá; po zavření stránky po něm nezůstane stopa.",
        en: "The file is not sent to any server and nothing is stored; once you close the page, nothing of it is left.",
      },
      {
        cs: "Česká verze CSV používá středníky a desetinnou čárku, takže ji Excel v češtině otevře rovnou do sloupců. Druhé tlačítko stáhne verzi s čárkami a desetinnou tečkou.",
        en: "The Czech CSV uses semicolons and a decimal comma, so Czech Excel opens it straight into columns. The other button downloads the version with commas and a decimal point.",
      },
      {
        cs: "Převodník čte záznamy 074 a 075 v kódování windows-1250; jeden soubor může obsahovat víc výpisů. Doplňkové záznamy se zprávou (078, 079) přeskočí.",
        en: "The converter reads records 074 and 075 encoded windows-1250; one file may hold several statements. It skips the extra message records (078, 079).",
      },
      {
        cs: "GPC neobsahuje měnu, proto ji CSV ani Excel neuvádí.",
        en: "GPC carries no currency, so neither the CSV nor the Excel file has a currency column.",
      },
      {
        cs: "Když zůstatky ve výpisu nesedí na obraty, převodník upozorní, ale pohyby do CSV zapíše.",
        en: "When the statement's balances don't agree with its turnovers, the converter warns you but still writes the movements to the CSV.",
      },
    ],
    faq: [
      {
        id: "bezpecnost",
        question: {
          cs: "Odešle se můj výpis někam?",
          en: "Is my statement sent anywhere?",
        },
        answer: {
          cs: "Ne. Stránka soubor přečte a převede přímo ve vašem prohlížeči a CSV vytvoří tamtéž. Na server Zettlu ani nikam jinam nic neodchází a nic se neukládá.",
          en: "No. The page reads and converts the file right in your browser and creates the CSV there too. Nothing goes to Zettl's server or anywhere else, and nothing is stored.",
        },
      },
      {
        id: "zdarma",
        question: {
          cs: "Stojí převod něco nebo potřebuji účet?",
          en: "Does it cost anything, or do I need an account?",
        },
        answer: {
          cs: "Ne. Převodník je zdarma a bez registrace.",
          en: "No. The converter is free and needs no sign-up.",
        },
      },
      {
        id: "excel",
        question: {
          cs: "Proč se mi CSV v Excelu otevře v jednom sloupci?",
          en: "Why does the CSV open in one column in Excel?",
        },
        answer: {
          cs: "Excel čte CSV podle oddělovače nastaveného v systému. Česká verze používá středníky, anglická čárky; stáhněte tu, která odpovídá vašemu Excelu.",
          en: "Excel reads CSV with the separator your system is set to. The Czech version uses semicolons, the English one commas; download the one that matches your Excel.",
        },
      },
      {
        id: "co-je-gpc",
        question: {
          cs: "Co je formát GPC (ABO)?",
          en: "What is the GPC (ABO) format?",
        },
        answer: {
          cs: "Textový formát elektronického výpisu s pevnou délkou řádků: hlavička výpisu se zůstatky a pak jeden řádek na každý pohyb. Fio ho nabízí ke stažení v internetbankingu.",
          en: "A fixed-width text format for electronic statements: a statement header with the balances, then one line per movement. Fio offers it for download in internet banking.",
        },
      },
    ],
    related: ["guideGpcImport", "guideFioApi", "guideMatching"],
  },

  // supabase/functions/process-inbox-email (PDF/PNG/JPEG/WEBP, 10 MB, 10 attachments),
  // src/lib/inbox/inbox.ts (<name>-<10 chars>@zettl.cz), _shared/multi-document.ts.
  // src/components/guides/IsdocViewer.tsx: readIsdocView (src/lib/isdoc/isdocView.ts) in the
  // browser, QR payment through buildPaymentQRPayload, print through window.print. No request.
  toolIsdocViewer: {
    key: "toolIsdocViewer",
    updated: "2026-11-03",
    seo: {
      title: {
        cs: "Prohlížeč faktur ISDOC zdarma: otevřete .isdoc a .isdocx v prohlížeči — Zettl",
        en: "Free ISDOC invoice viewer: open .isdoc and .isdocx files in your browser — Zettl",
      },
      description: {
        cs: "Otevřete fakturu ISDOC (.isdoc, .isdocx) a přečtěte si ji jako běžnou fakturu: dodavatel, odběratel, položky, rekapitulace DPH, platební údaje a QR platba. Vytisknete ji nebo uložíte jako PDF. Zdarma, soubor se nikam neodesílá.",
        en: "Open an ISDOC invoice (.isdoc, .isdocx) and read it like an ordinary invoice: supplier, customer, items, VAT summary, payment details and a QR payment. Print it or save it as PDF. Free; the file is never sent anywhere.",
      },
    },
    name: { cs: "Prohlížeč faktur ISDOC", en: "ISDOC invoice viewer" },
    eyebrow: { cs: "Nástroj zdarma", en: "Free tool" },
    h1: {
      cs: "Prohlížeč faktur ISDOC (.isdoc, .isdocx)",
      en: "ISDOC invoice viewer (.isdoc, .isdocx)",
    },
    lead: {
      cs: "Dostali jste fakturu jako soubor .isdoc nebo .isdocx a nemáte ji v čem otevřít? Nahrajte ji sem a uvidíte ji jako běžnou fakturu: dodavatele, odběratele, položky, rekapitulaci DPH a platební údaje s QR platbou. Vytisknete ji nebo uložíte jako PDF. Zdarma, bez registrace a soubor se nikam neodesílá.",
      en: "Got an invoice as an .isdoc or .isdocx file and nothing to open it with? Load it here and see it as an ordinary invoice: supplier, customer, items, VAT summary and payment details with a QR payment. Print it or save it as PDF. Free, no sign-up, and the file is never sent anywhere.",
    },
    freeTool: {
      summary: {
        cs: "Otevře fakturu .isdoc nebo .isdocx jako čitelný doklad s rekapitulací DPH a QR platbou; vytisknete ji nebo uložíte jako PDF.",
        en: "Opens an .isdoc or .isdocx invoice as a readable document with a VAT summary and a QR payment; print it or save it as PDF.",
      },
      bridge: {
        title: {
          cs: "Pracujete s fakturami v ISDOC pravidelně?",
          en: "Working with ISDOC invoices regularly?",
        },
        text: {
          cs: "Zettl vydané faktury do ISDOC exportuje a v tarifu Firma je z ISDOC i importuje. Příchozí platby z Fio s nimi páruje podle variabilního symbolu.",
          en: "Zettl exports issued invoices to ISDOC, and on the Firma plan it imports them from ISDOC too. It matches incoming Fio payments with them by variable symbol.",
        },
        guide: "guideIsdoc",
      },
    },
    tool: "isdocViewer",
    steps: [
      {
        title: { cs: "Vyberte soubor", en: "Pick the file" },
        text: {
          cs: "Přetáhněte fakturu .isdoc nebo .isdocx výše, nebo ji vyberte. Funguje i .xml ve formátu ISDOC.",
          en: "Drop the .isdoc or .isdocx invoice above, or choose it. An .xml in ISDOC format works too.",
        },
      },
      {
        title: { cs: "Přečtěte si fakturu", en: "Read the invoice" },
        text: {
          cs: "Uvidíte dodavatele a odběratele s IČO a DIČ, data, položky, rekapitulaci DPH podle sazeb a částku k úhradě.",
          en: "You see the supplier and customer with their company and VAT IDs, the dates, the items, the VAT summary by rate and the amount to pay.",
        },
      },
      {
        title: { cs: "Zaplaťte přes QR", en: "Pay by QR" },
        text: {
          cs: "Když faktura v korunách obsahuje český nebo slovenský účet, prohlížeč z něj vytvoří QR platbu s částkou, variabilním symbolem a splatností.",
          en: "When a CZK invoice carries a Czech or Slovak account, the viewer turns it into a QR payment with the amount, variable symbol and due date.",
        },
      },
      {
        title: { cs: "Vytiskněte nebo uložte PDF", en: "Print or save as PDF" },
        text: {
          cs: "Tlačítko Vytisknout otevře tisk jen s fakturou; v něm zvolíte tiskárnu nebo Uložit jako PDF.",
          en: "The Print button opens printing with just the invoice; choose a printer or Save as PDF there.",
        },
      },
    ],
    notes: [
      {
        cs: "Soubor se neodesílá na žádný server a nic se neukládá; po zavření stránky po něm nezůstane stopa.",
        en: "The file is not sent to any server and nothing is stored; once you close the page, nothing of it is left.",
      },
      {
        cs: "Prohlížeč ukazuje, co je v souboru. Nekontroluje, jestli je faktura věcně a daňově správně.",
        en: "The viewer shows what the file contains. It does not check whether the invoice is correct in substance or for tax.",
      },
      {
        cs: "Když soubor IBAN neobsahuje, prohlížeč ho dopočítá z českého čísla účtu a u platby to uvede.",
        en: "When the file has no IBAN, the viewer computes it from the Czech account number and says so next to the payment.",
      },
      {
        cs: "QR platba se tvoří jen u faktur v korunách s českým nebo slovenským účtem a jen pro platbu převodem.",
        en: "A QR payment is only made for CZK invoices with a Czech or Slovak account, and only for bank transfers.",
      },
      {
        cs: "Soubor .isdocx je ZIP s fakturou .isdoc a jejím PDF; prohlížeč čte fakturu .isdoc.",
        en: "An .isdocx file is a ZIP holding the .isdoc invoice and its PDF; the viewer reads the .isdoc invoice.",
      },
    ],
    faq: [
      {
        id: "bezpecnost",
        question: { cs: "Odešle se moje faktura někam?", en: "Is my invoice sent anywhere?" },
        answer: {
          cs: "Ne. Stránka soubor přečte přímo ve vašem prohlížeči. Na server Zettlu ani nikam jinam nic neodchází a nic se neukládá.",
          en: "No. The page reads the file right in your browser. Nothing goes to Zettl's server or anywhere else, and nothing is stored.",
        },
      },
      {
        id: "co-je-isdoc",
        question: {
          cs: "Co je soubor .isdoc a .isdocx?",
          en: "What are .isdoc and .isdocx files?",
        },
        answer: {
          cs: "ISDOC je český standard elektronické faktury ve formátu XML. Soubor .isdoc je samotná faktura, .isdocx je ZIP, ve kterém je faktura .isdoc a její PDF. Účetní programy je načtou bez přepisování.",
          en: "ISDOC is the Czech standard for e-invoices in XML. An .isdoc file is the invoice itself; an .isdocx is a ZIP holding the .isdoc invoice and its PDF. Accounting software loads them without retyping.",
        },
      },
      {
        id: "pdf",
        question: { cs: "Jak z ISDOC udělám PDF?", en: "How do I turn an ISDOC file into a PDF?" },
        answer: {
          cs: "Otevřete fakturu v prohlížeči, klikněte na Vytisknout / uložit jako PDF a v tiskovém dialogu zvolte Uložit jako PDF.",
          en: "Open the invoice in the viewer, click Print / save as PDF and choose Save as PDF in the print dialog.",
        },
      },
      {
        id: "zdarma",
        question: {
          cs: "Stojí prohlížeč něco nebo potřebuji účet?",
          en: "Does the viewer cost anything, or do I need an account?",
        },
        answer: {
          cs: "Ne. Prohlížeč je zdarma a bez registrace.",
          en: "No. The viewer is free and needs no sign-up.",
        },
      },
    ],
    related: ["guideIsdoc", "toolQrPayment", "toolGpcToCsv"],
  },

  // src/components/guides/QrPaymentGenerator.tsx: buildQrPayment (src/lib/qr/qrPayment.ts) and
  // the shared buildSPD in the browser, qrcode for PNG/SVG. No request.
  toolQrPayment: {
    key: "toolQrPayment",
    updated: "2026-11-03",
    seo: {
      title: {
        cs: "Generátor QR platby zdarma: QR kód pro platbu na účet — Zettl",
        en: "Free Czech QR payment generator: a QR code for a bank transfer — Zettl",
      },
      description: {
        cs: "Vytvořte QR platbu ve formátu SPD z čísla účtu nebo IBAN, částky, variabilního symbolu a splatnosti. Stáhnete ji jako PNG nebo SVG. Zdarma, bez registrace, nic se nikam neodesílá.",
        en: "Make a Czech QR payment (SPD) from an account number or IBAN, amount, variable symbol and due date. Download it as PNG or SVG. Free, no sign-up, nothing is sent anywhere.",
      },
    },
    name: { cs: "Generátor QR platby", en: "QR payment generator" },
    eyebrow: { cs: "Nástroj zdarma", en: "Free tool" },
    h1: {
      cs: "Generátor QR platby (QR kód pro platbu)",
      en: "Czech QR payment generator",
    },
    lead: {
      cs: "Vyplňte číslo účtu, částku a variabilní symbol a hned uvidíte QR kód, který naskenuje mobilní bankovnictví českých bank. Stáhnete ho jako PNG nebo SVG, třeba do faktury nebo e-mailu. Zdarma, bez registrace; nic z formuláře se nikam neodesílá.",
      en: "Fill in the account number, amount and variable symbol and see the QR code Czech banking apps scan, right away. Download it as PNG or SVG, for an invoice or an e-mail. Free, no sign-up; nothing from the form is sent anywhere.",
    },
    freeTool: {
      summary: {
        cs: "Z čísla účtu, částky a symbolů vytvoří QR platbu (SPD) ke stažení jako PNG nebo SVG.",
        en: "Makes a Czech QR payment (SPD) from the account, amount and symbols, to download as PNG or SVG.",
      },
      bridge: {
        title: { cs: "Vystavujete faktury?", en: "Issuing invoices?" },
        text: {
          cs: "Faktury vystavené v Zettlu nesou QR platbu. Když zákazník zaplatí, Zettl platbu z Fio spáruje s fakturou podle variabilního symbolu.",
          en: "Invoices issued in Zettl carry a QR payment. When the customer pays, Zettl matches the payment downloaded from Fio with the invoice by variable symbol.",
        },
        guide: "guideMatching",
      },
    },
    tool: "qrPayment",
    steps: [
      {
        title: { cs: "Vyplňte účet a částku", en: "Fill in the account and amount" },
        text: {
          cs: "Číslo účtu s kódem banky (např. 19-2000145399/0800) nebo IBAN. Číslo účtu se ověří kontrolním součtem a převede na IBAN.",
          en: "A Czech account number with bank code (e.g. 19-2000145399/0800) or an IBAN. The account number is checked with its check digits and turned into an IBAN.",
        },
      },
      {
        title: { cs: "Doplňte symboly a splatnost", en: "Add symbols and the due date" },
        text: {
          cs: "Variabilní, konstantní a specifický symbol, datum splatnosti, jméno příjemce a zprávu vyplníte, jen když je potřebujete.",
          en: "The variable, constant and specific symbols, due date, recipient name and message are filled in only when you need them.",
        },
      },
      {
        title: { cs: "Stáhněte QR kód", en: "Download the QR code" },
        text: {
          cs: "QR kód se mění s každým vyplněným polem. Stáhnete ho jako PNG nebo SVG, nebo zkopírujete jeho text (SPD).",
          en: "The QR code updates with every field. Download it as PNG or SVG, or copy its text (SPD).",
        },
      },
    ],
    notes: [
      {
        cs: "Nic z formuláře se neodesílá na žádný server a nic se neukládá.",
        en: "Nothing from the form is sent to any server and nothing is stored.",
      },
      {
        cs: "QR platba je ve formátu SPD 1.0, který čtou mobilní aplikace českých bank. Generátor bere české a slovenské účty.",
        en: "The QR payment follows the SPD 1.0 format Czech banking apps read. The generator takes Czech and Slovak accounts.",
      },
      {
        cs: "Diakritiku QR platba nepřenese, proto ji zpráva a jméno příjemce ztratí (Nájem → NAJEM).",
        en: "QR payments carry no accents, so the message and recipient name lose them (Nájem → NAJEM).",
      },
      {
        cs: "Generátor ověří tvar čísla účtu a IBAN, ne to, že účet existuje nebo patří tomu, komu platíte.",
        en: "The generator checks the shape of the account number and IBAN, not that the account exists or belongs to whoever you pay.",
      },
    ],
    faq: [
      {
        id: "bezpecnost",
        question: { cs: "Odešlou se moje údaje někam?", en: "Is anything I type sent anywhere?" },
        answer: {
          cs: "Ne. QR kód vzniká přímo ve vašem prohlížeči. Na server Zettlu ani nikam jinam nic neodchází a nic se neukládá.",
          en: "No. The QR code is made right in your browser. Nothing goes to Zettl's server or anywhere else, and nothing is stored.",
        },
      },
      {
        id: "co-je-spd",
        question: { cs: "Co je QR platba (SPD)?", en: "What is a Czech QR payment (SPD)?" },
        answer: {
          cs: "QR platba je český standard Short Payment Descriptor: text s číslem účtu jako IBAN, částkou, měnou a symboly, zakódovaný do QR kódu. Mobilní bankovnictví ho naskenuje a předvyplní platbu.",
          en: "A Czech QR payment follows the Short Payment Descriptor standard: text with the account as an IBAN, the amount, the currency and the symbols, encoded in a QR code. Banking apps scan it and fill in the payment.",
        },
      },
      {
        id: "png-svg",
        question: { cs: "Mám stáhnout PNG, nebo SVG?", en: "Should I download PNG or SVG?" },
        answer: {
          cs: "PNG vložíte do e-mailu, dokumentu nebo na web. SVG je vektorové, takže zůstane ostré při jakékoli velikosti, třeba v tiskovém podkladu.",
          en: "PNG goes into an e-mail, a document or a web page. SVG is vector, so it stays sharp at any size, for example in print.",
        },
      },
      {
        id: "zdarma",
        question: {
          cs: "Stojí generátor něco nebo potřebuji účet?",
          en: "Does the generator cost anything, or do I need an account?",
        },
        answer: {
          cs: "Ne. Generátor je zdarma a bez registrace.",
          en: "No. The generator is free and needs no sign-up.",
        },
      },
    ],
    related: ["toolIsdocViewer", "guideIsdoc", "guideMatching"],
  },

  // src/components/guides/AccountIbanConverter.tsx: convertAccount / convertBulk
  // (src/lib/banking/accountConvert.ts) and the ČNB code list (czechBanks.ts). No request.
  toolAccountIban: {
    key: "toolAccountIban",
    updated: "2026-11-03",
    seo: {
      title: {
        cs: "Převod čísla účtu na IBAN a zpět, s kontrolou — zdarma — Zettl",
        en: "Czech account number to IBAN and back, with validation — free — Zettl",
      },
      description: {
        cs: "Převeďte české číslo účtu na IBAN a IBAN zpět na číslo účtu. Ověří kontrolní číslice (modulo 11 i IBAN), doplní banku a BIC podle číselníku ČNB. I hromadně s exportem do CSV. Zdarma, nic se nikam neodesílá.",
        en: "Convert a Czech account number to an IBAN and an IBAN back to the account. Checks the check digits (modulo 11 and IBAN) and adds the bank and BIC from the Czech National Bank's list. In bulk too, with a CSV export. Free; nothing is sent anywhere.",
      },
    },
    name: { cs: "Převod čísla účtu na IBAN", en: "Account number to IBAN" },
    eyebrow: { cs: "Nástroj zdarma", en: "Free tool" },
    h1: {
      cs: "Převod čísla účtu na IBAN a zpět",
      en: "Czech account number to IBAN and back",
    },
    lead: {
      cs: "Napište číslo účtu s kódem banky (např. 19-2000145399/0800) a hned uvidíte IBAN, banku a BIC; z IBAN zase dostanete číslo účtu. Nástroj ověří kontrolní číslice, takže překlep v čísle odhalí. Víc účtů najednou vložíte po řádcích a stáhnete jako CSV. Zdarma a nic se nikam neodesílá.",
      en: "Type a Czech account number with its bank code (e.g. 19-2000145399/0800) and see the IBAN, bank and BIC at once; an IBAN gives you the account number back. The tool checks the check digits, so it catches a typo in the number. Paste many accounts one per line and download them as CSV. Free, and nothing is sent anywhere.",
    },
    freeTool: {
      summary: {
        cs: "Převede české číslo účtu na IBAN a zpět, ověří kontrolní číslice a doplní banku a BIC; i hromadně do CSV.",
        en: "Converts a Czech account number to an IBAN and back, checks the check digits and adds the bank and BIC; in bulk to CSV too.",
      },
      bridge: {
        title: { cs: "Dohledáváte, komu jste platili?", en: "Tracing who you paid?" },
        text: {
          cs: "Zettl stahuje platby z Fio i s účtem a názvem protistrany a ke každé hledá doklad podle částky, data a dodavatele. Co se nespáruje, vidíte hned.",
          en: "Zettl downloads your Fio payments with the counterparty's account and name and looks for each one's document by amount, date and vendor. What doesn't match shows straight away.",
        },
        guide: "guideMatching",
      },
    },
    tool: "accountIban",
    steps: [
      {
        title: { cs: "Napište číslo účtu nebo IBAN", en: "Type the account or IBAN" },
        text: {
          cs: "Číslo účtu s předčíslím nebo bez a s kódem banky za lomítkem, nebo IBAN s mezerami či bez.",
          en: "An account number with or without its prefix and the bank code after a slash, or an IBAN with or without spaces.",
        },
      },
      {
        title: { cs: "Zkontrolujte výsledek", en: "Check the result" },
        text: {
          cs: "Uvidíte číslo účtu, IBAN, banku a BIC a jestli kontrolní číslice sedí.",
          en: "You see the account number, IBAN, bank and BIC, and whether the check digits hold.",
        },
      },
      {
        title: { cs: "Hromadně do CSV", en: "In bulk to CSV" },
        text: {
          cs: "Vložte víc účtů po řádcích; výsledek stáhnete jako CSV pro Excel.",
          en: "Paste several accounts one per line; download the result as CSV for Excel.",
        },
      },
    ],
    notes: [
      {
        cs: "Nic se neodesílá na žádný server a nic se neukládá.",
        en: "Nothing is sent to any server and nothing is stored.",
      },
      {
        cs: "Kontrola ověří tvar čísla a jeho kontrolní číslice (modulo 11 podle vyhlášky ČNB, u IBAN modulo 97). Neověří, že účet existuje nebo komu patří.",
        en: "The check covers the shape of the number and its check digits (modulo 11 under the Czech National Bank's decree, modulo 97 for an IBAN). It does not check that the account exists or whose it is.",
      },
      {
        cs: `Banky a BIC jsou z číselníku kódů bank ČNB (stažen ${"3. 11. 2026"}).`,
        en: "Banks and BICs come from the Czech National Bank's list of bank codes (downloaded on 3 November 2026).",
      },
      {
        cs: "Zahraniční IBAN nástroj ověří, na české číslo účtu ho ale převést nejde.",
        en: "A foreign IBAN is checked, but it cannot become a Czech account number.",
      },
    ],
    faq: [
      {
        id: "bezpecnost",
        question: {
          cs: "Odešlou se čísla účtů někam?",
          en: "Are the account numbers sent anywhere?",
        },
        answer: {
          cs: "Ne. Převod i kontrola proběhnou přímo ve vašem prohlížeči. Na server Zettlu ani nikam jinam nic neodchází a nic se neukládá.",
          en: "No. The conversion and the check run right in your browser. Nothing goes to Zettl's server or anywhere else, and nothing is stored.",
        },
      },
      {
        id: "jak-vznika-iban",
        question: { cs: "Jak vzniká český IBAN?", en: "How is a Czech IBAN made?" },
        answer: {
          cs: "CZ, dvě kontrolní číslice, čtyřmístný kód banky, šestimístné předčíslí a desetimístné číslo účtu, obojí doplněné zleva nulami. Kontrolní číslice se počítají metodou modulo 97.",
          en: "CZ, two check digits, the four-digit bank code, the six-digit prefix and the ten-digit account number, both padded with zeros on the left. The check digits are computed with modulo 97.",
        },
      },
      {
        id: "neplatne",
        question: {
          cs: "Proč mi vyjde, že číslo účtu není platné?",
          en: "Why does it say my account number is not valid?",
        },
        answer: {
          cs: "České číslo účtu má kontrolní součet (modulo 11). Když nesedí, je v čísle nejspíš překlep nebo přehozené číslice. Zkontrolujte i kód banky za lomítkem.",
          en: "A Czech account number has a checksum (modulo 11). When it fails, there is most likely a typo or swapped digits. Check the bank code after the slash too.",
        },
      },
      {
        id: "zdarma",
        question: {
          cs: "Stojí nástroj něco nebo potřebuji účet?",
          en: "Does the tool cost anything, or do I need an account?",
        },
        answer: {
          cs: "Ne. Nástroj je zdarma a bez registrace.",
          en: "No. The tool is free and needs no sign-up.",
        },
      },
    ],
    related: ["toolQrPayment", "guideFioApi", "toolGpcToCsv"],
  },

  // src/components/guides/GpcToPohodaConverter.tsx: parseGpc + gpcToPohoda
  // (src/lib/tools/gpcToPohoda.ts, the export's own bank builder). No request.
  toolGpcPohoda: {
    key: "toolGpcPohoda",
    updated: "2026-11-03",
    seo: {
      title: {
        cs: "Výpis GPC do POHODY: převod bankovního výpisu na XML import zdarma — Zettl",
        en: "GPC statement to POHODA: a bank statement as an XML import, free — Zettl",
      },
      description: {
        cs: "Převeďte výpis GPC / ABO z Fio banky na XML import bankovních pohybů do POHODY (agenda Banka). Zdarma, bez registrace; soubor se nikam neodesílá, převod proběhne ve vašem prohlížeči.",
        en: "Turn a GPC / ABO statement from Fio banka into an XML import of bank movements for POHODA (Bank agenda). Free, no sign-up; the file is never sent anywhere, the conversion runs in your browser.",
      },
    },
    name: { cs: "Výpis GPC do POHODY", en: "GPC statement to POHODA" },
    eyebrow: { cs: "Nástroj zdarma", en: "Free tool" },
    h1: {
      cs: "Výpis GPC / ABO do POHODY (XML import)",
      en: "GPC / ABO statement to POHODA (XML import)",
    },
    lead: {
      cs: "Nahrajte výpis GPC z Fio banky, zadejte IČO firmy a stáhněte XML, které v POHODĚ naimportujete do agendy Banka jako bankovní pohyby. XML vzniká stejným kódem jako export Zettlu pro POHODU a ověřujeme ho proti schématům Stormware. Zdarma, bez registrace a soubor se nikam neodesílá.",
      en: "Load a GPC statement from Fio banka, enter the company ID (IČO) and download an XML you import in POHODA into the Bank agenda as bank movements. The XML is built by the same code as Zettl's POHODA export and checked against Stormware's schemas. Free, no sign-up, and the file is never sent anywhere.",
    },
    freeTool: {
      summary: {
        cs: "Výpis GPC / ABO z Fio převede na XML import bankovních pohybů do POHODY.",
        en: "Turns a Fio GPC / ABO statement into an XML import of bank movements for POHODA.",
      },
      bridge: {
        title: {
          cs: "Do POHODY každý měsíc bez převádění?",
          en: "Into POHODA every month without converting?",
        },
        text: {
          cs: "Zettl stahuje platby z Fio přes API, páruje je s doklady a za měsíc připraví XML pro import do POHODY. Je-li POHODA mServer dostupný z internetu, pošle měsíc do POHODY i přímo.",
          en: "Zettl downloads your Fio payments through the API, matches them with documents and prepares the month as XML for import into POHODA. If POHODA mServer is reachable from the internet, it sends the month into POHODA directly.",
        },
        guide: "guidePohoda",
      },
    },
    tool: "gpcPohoda",
    steps: [
      {
        title: { cs: "Stáhněte výpis ve Fio", en: "Download the statement at Fio" },
        text: {
          cs: "V internetbankingu Fio vyberete výpis a formát GPC (ABO).",
          en: "In Fio internet banking choose the statement and the GPC (ABO) format.",
        },
      },
      {
        title: { cs: "Nahrajte ho a zadejte IČO", en: "Load it and enter the IČO" },
        text: {
          cs: "IČO musí odpovídat firmě v POHODĚ. Zkratku bankovního účtu z POHODY vyplníte, aby pohyby šly na správný účet.",
          en: "The IČO must match the company in POHODA. Fill in the bank account's abbreviation from POHODA so the movements land on the right account.",
        },
      },
      {
        title: { cs: "Stáhněte XML", en: "Download the XML" },
        text: {
          cs: "Jeden bankovní pohyb (příjem nebo výdej) na každou položku výpisu, se symboly, protiúčtem a protistranou.",
          en: "One bank movement (receipt or expense) per statement line, with the symbols, counter account and counterparty.",
        },
      },
      {
        title: { cs: "Naimportujte v POHODĚ", en: "Import it in POHODA" },
        text: {
          cs: "V POHODĚ přes Soubor / Datová komunikace / XML import/export, jak ukazuje náš návod.",
          en: "In POHODA via File / Data communication / XML import/export, as our guide shows.",
        },
      },
    ],
    notes: [
      {
        cs: "Soubor se neodesílá na žádný server a nic se neukládá.",
        en: "The file is not sent to any server and nothing is stored.",
      },
      {
        cs: "GPC neobsahuje měnu, pohyby proto vzniknou v korunách. Pro účet v cizí měně nástroj není.",
        en: "GPC carries no currency, so the movements are in CZK. The tool is not for a foreign-currency account.",
      },
      {
        cs: "Stejný výpis naimportovaný dvakrát vytvoří pohyby dvakrát; POHODA je podle čísla neodmítne.",
        en: "Importing the same statement twice creates the movements twice; POHODA does not reject them by number.",
      },
      {
        cs: "Nástroj pohyby k dokladům nepáruje. To dělá Zettl, který do POHODY pošle i doklady a párový symbol.",
        en: "The tool does not match movements with documents. Zettl does, and sends POHODA the documents and the pairing symbol too.",
      },
    ],
    faq: [
      {
        id: "bezpecnost",
        question: { cs: "Odešle se můj výpis někam?", en: "Is my statement sent anywhere?" },
        answer: {
          cs: "Ne. Výpis přečte a převede jen váš prohlížeč. Na server Zettlu ani nikam jinam nic neodchází a nic se neukládá.",
          en: "No. Only your browser reads and converts the statement. Nothing goes to Zettl's server or anywhere else, and nothing is stored.",
        },
      },
      {
        id: "ico",
        question: { cs: "Proč potřebujete IČO?", en: "Why do you need the IČO?" },
        answer: {
          cs: "POHODA naimportuje XML jen do účetní jednotky se stejným IČO, jaké je v souboru. Bez něj by import skončil chybou.",
          en: "POHODA imports an XML only into the accounting unit with the same IČO as the file. Without it the import fails.",
        },
      },
      {
        id: "schema",
        question: { cs: "Načte POHODA ten soubor?", en: "Will POHODA load the file?" },
        answer: {
          cs: "XML odpovídá schématům Stormware verze 2 (agenda Banka) a je v kódování Windows-1250, jako vzorové soubory POHODY. Vyzkoušejte import nejdřív na jednom výpisu.",
          en: "The XML follows Stormware's version 2 schemas (Bank agenda) and is encoded Windows-1250, like POHODA's sample files. Try the import on one statement first.",
        },
      },
      {
        id: "zdarma",
        question: {
          cs: "Stojí nástroj něco nebo potřebuji účet?",
          en: "Does the tool cost anything, or do I need an account?",
        },
        answer: {
          cs: "Ne. Nástroj je zdarma a bez registrace.",
          en: "No. The tool is free and needs no sign-up.",
        },
      },
    ],
    related: ["guidePohodaHowTo", "guidePohoda", "toolGpcToCsv"],
  },

  // A month-end checklist for sole traders with Fio. Every product claim is one
  // the other guides already make (Fio API 90 days, inbox forwarding, matching by
  // amount/date/vendor and by variable symbol, Chybějící doklady suggestions,
  // exports, POHODA, the monthly ZIP). No tax deadlines: they depend on the
  // person's registrations, so the page points to Finanční správa instead.
  guideMonthClose: {
    key: "guideMonthClose",
    updated: "2026-11-04",
    seo: {
      title: {
        cs: "Měsíční uzávěrka pro OSVČ s Fio: checklist v 6 krocích — Zettl",
        en: "Monthly close for sole traders with Fio: a 6-step checklist — Zettl",
      },
      description: {
        cs: "Na konci měsíce: pohyby z Fio, doklady ke každé platbě, kontrola vydaných faktur a předání účetní. Checklist v 6 krocích, s nástroji zdarma i bez nich.",
        en: "At month end: Fio movements, a document for every payment, a check of issued invoices and the hand-off to your accountant. A 6-step checklist, with free tools or without.",
      },
    },
    name: { cs: "Měsíční uzávěrka: checklist", en: "Monthly close: a checklist" },
    eyebrow: { cs: "Pro OSVČ", en: "For sole traders" },
    h1: {
      cs: "Měsíční uzávěrka pro OSVČ s Fio: checklist",
      en: "Monthly close for sole traders with Fio: a checklist",
    },
    lead: {
      cs: "Uzávěrka měsíce znamená mít všechny pohyby z účtu u Fio, ke každé odchozí platbě doklad, spárované vydané faktury a vše předané účetní. Projděte šest kroků níže; zvládnete je ručně s výpisem a nástroji zdarma, nebo je Zettl udělá průběžně za vás.",
      en: "Closing the month means having every movement from your Fio account, a document for every outgoing payment, issued invoices matched, and everything handed to your accountant. Go through the six steps below; you can do them by hand with a statement and free tools, or Zettl does them for you as the month goes.",
    },
    steps: [
      {
        title: { cs: "Pohyby z Fio za celý měsíc", en: "The whole month's Fio movements" },
        text: {
          cs: "Ručně: stáhněte ve Fio výpis GPC/ABO a převeďte ho do Excelu nástrojem zdarma. V Zettlu: pohyby chodí přes Fio API každé ráno samy.",
          en: "By hand: download a GPC/ABO statement at Fio and turn it into Excel with the free tool. In Zettl: the movements come in through the Fio API every morning.",
        },
      },
      {
        title: { cs: "Doklady na jedno místo", en: "Documents in one place" },
        text: {
          cs: "Přijaté faktury a účtenky z e-mailu, papíru i e-shopů. Do Zettlu je přepošlete na e-mailovou adresu firmy; účtenku z papíru vyfotíte.",
          en: "Received invoices and receipts from e-mail, paper and e-shops. In Zettl you forward them to the company's e-mail address; a paper receipt is a photo.",
        },
      },
      {
        title: { cs: "Ke každé platbě doklad", en: "A document for every payment" },
        text: {
          cs: "Projděte odchozí platby a ke každé najděte doklad podle částky, data a dodavatele. Zettl je páruje sám a co nesedí, nechá otevřené.",
          en: "Go through the outgoing payments and find each one's document by amount, date and vendor. Zettl matches them itself and leaves what doesn't fit open.",
        },
      },
      {
        title: { cs: "Dohledejte, co chybí", en: "Chase what is missing" },
        text: {
          cs: "Platby bez dokladu sepište a dohledejte. V Zettlu je najdete na stránce Chybějící doklady; bankovní poplatky, platby daní a pojištění na účty u ČNB a převody mezi vlastními účty navrhne označit jako platby bez dokladu.",
          en: "List the payments without a document and track them down. In Zettl they're on the Missing documents page; bank fees, tax and insurance payments to accounts at the Czech National Bank and transfers between your own accounts are suggested as needing no document.",
        },
      },
      {
        title: {
          cs: "Vydané faktury a příchozí platby",
          en: "Issued invoices and incoming payments",
        },
        text: {
          cs: "Zkontrolujte, které faktury zákazníci zaplatili. Zettl příchozí platby páruje s vydanými fakturami podle variabilního symbolu.",
          en: "Check which invoices your customers have paid. Zettl matches incoming payments with issued invoices by variable symbol.",
        },
      },
      {
        title: { cs: "Předejte měsíc účetní", en: "Hand the month to your accountant" },
        text: {
          cs: "Doklady jako PDF, platby jako CSV, vydané faktury jako ISDOC nebo XLSX, pro POHODU XML. Zettl může měsíc poslat účetní e-mailem sám nebo ho odeslat do POHODY přes mServer.",
          en: "Documents as PDF, payments as CSV, issued invoices as ISDOC or XLSX, XML for POHODA. Zettl can e-mail the month to your accountant by itself or send it into POHODA through mServer.",
        },
      },
    ],
    notes: [
      {
        cs: "Zettl nevede účetnictví ani nepodává přiznání. Připraví spárované platby a doklady; účtování a daně řeší vaše účetní.",
        en: "Zettl doesn't keep the books or file returns. It prepares matched payments and documents; your accountant handles bookkeeping and taxes.",
      },
      {
        cs: "Termíny přiznání a záloh závisí na tom, k čemu jste registrovaní. Platné termíny najdete v daňovém kalendáři Finanční správy.",
        en: "Filing and advance deadlines depend on what you're registered for. The current dates are in the Finanční správa (Czech tax administration) tax calendar.",
      },
      {
        cs: "Fio API vydá bez další autorizace pohyby jen za posledních 90 dní. Na uzávěrku starších měsíců použijte výpis GPC/ABO.",
        en: "Without further authorization the Fio API releases only the last 90 days of movements. For older months, use a GPC/ABO statement.",
      },
      {
        cs: "Zda konkrétní platba doklad potřebuje, rozhodne nakonec účetní. Zettl jen navrhuje.",
        en: "Whether a given payment needs a document is your accountant's call in the end. Zettl only suggests.",
      },
    ],
    sources: {
      checkedOn: "2026-11-04",
      intro: {
        cs: "Odkaz na daňové termíny jsme ověřili dne {date}: ",
        en: "We checked the link to the tax dates on {date}: ",
      },
      links: [
        {
          label: "Daňový kalendář Finanční správy",
          url: "https://financnisprava.gov.cz/cs/danovy-kalendar",
        },
      ],
    },
    faq: [
      {
        id: "kdy",
        question: {
          cs: "Kdy uzávěrku dělat?",
          en: "When should I close the month?",
        },
        answer: {
          cs: "Po posledním dni měsíce, jakmile máte všechny pohyby z Fio, a dřív, než podklady potřebuje vaše účetní. Na termínu se domluvte s ní.",
          en: "After the last day of the month, once you have all the Fio movements, and before your accountant needs the documents. Agree the date with them.",
        },
      },
      {
        id: "bez-zettlu",
        question: {
          cs: "Zvládnu to bez Zettlu?",
          en: "Can I do this without Zettl?",
        },
        answer: {
          cs: "Ano. Výpis GPC/ABO z Fio převedete do Excelu nástrojem zdarma a doklady k platbám přiřadíte ručně v tabulce. Zettl dělá kroky 1 až 5 průběžně sám a krok 6 umí poslat automaticky.",
          en: "Yes. Turn a Fio GPC/ABO statement into Excel with the free tool and match documents to payments by hand in the spreadsheet. Zettl does steps 1 to 5 as the month goes and can send step 6 automatically.",
        },
      },
      {
        id: "chybi-doklad",
        question: {
          cs: "Co když doklad k platbě nenajdu?",
          en: "What if I can't find a payment's document?",
        },
        answer: {
          cs: "Požádejte dodavatele o kopii; řada faktur jde stáhnout i z jejich zákaznických účtů. Než doklad dodáte, nechte platbu otevřenou a dejte o ní vědět účetní.",
          en: "Ask the vendor for a copy; many invoices can also be downloaded from their customer accounts. Until you have it, leave the payment open and tell your accountant about it.",
        },
      },
      {
        id: "pohoda",
        question: {
          cs: "Jak dostanu měsíc do POHODY?",
          en: "How do I get the month into POHODA?",
        },
        answer: {
          cs: "Bankovní pohyby z výpisu GPC převedete na XML pro POHODU nástrojem zdarma. Zettl připraví XML s přijatými doklady i pohyby, nebo měsíc pošle do POHODY přímo přes mServer.",
          en: "The free tool turns a GPC statement's bank movements into XML for POHODA. Zettl prepares XML with the received documents and the movements, or sends the month into POHODA directly through mServer.",
        },
      },
    ],
    related: ["toolGpcToCsv", "guideMatching", "toolGpcPohoda", "guideAccountant"],
  },
  guideEmailInbox: {
    key: "guideEmailInbox",
    updated: "2026-11-02",
    seo: {
      title: {
        cs: "Účtenky a faktury e-mailem: adresa pro doklady — Zettl",
        en: "Receipts and invoices by e-mail: an address for documents — Zettl",
      },
      description: {
        cs: "Každá firma v Zettlu má vlastní adresu @zettl.cz. Přepošlete na ni e-mail s účtenkou nebo fakturou a AI z ní přečte dodavatele, datum a částku.",
        en: "Every company in Zettl gets its own @zettl.cz address. Forward an e-mail with a receipt or invoice and AI reads the vendor, date and amount.",
      },
    },
    name: { cs: "Účtenky a faktury e-mailem", en: "Receipts and invoices by e-mail" },
    eyebrow: { cs: "Doklady", en: "Documents" },
    h1: {
      cs: "Účtenky a faktury stačí přeposlat e-mailem",
      en: "Just forward your receipts and invoices by e-mail",
    },
    lead: {
      cs: "Každá firma v Zettlu má vlastní e-mailovou adresu @zettl.cz. Faktury, které vám chodí e-mailem, na ni přepošlete; AI z přílohy přečte dodavatele, datum a částku a Zettl doklad spáruje s platbou.",
      en: "Every company in Zettl has its own @zettl.cz e-mail address. Forward the invoices you receive by e-mail to it; AI reads the vendor, date and amount from the attachment, and Zettl matches the document with its payment.",
    },
    steps: [
      {
        title: { cs: "Zapněte adresu", en: "Switch on the address" },
        text: {
          cs: "Správce firmy ji zapne v Nastavení → Banky a integrace. Adresa má tvar název-xxxxxxxxxx@zettl.cz a sdílí ji celá firma.",
          en: "A company admin switches it on under Settings → Banks & integrations. It looks like name-xxxxxxxxxx@zettl.cz and the whole company shares it.",
        },
      },
      {
        title: { cs: "Přepošlete doklad", en: "Forward the document" },
        text: {
          cs: "Přepošlete e-mail s přílohou PDF, JPG, PNG nebo WEBP. V jednom e-mailu může být až 10 příloh.",
          en: "Forward the e-mail with a PDF, JPG, PNG or WEBP attachment. One e-mail can carry up to 10 attachments.",
        },
      },
      {
        title: { cs: "AI doklad přečte", en: "AI reads the document" },
        text: {
          cs: "Z každé přílohy vyčte dodavatele, datum a částku. PDF s několika fakturami rozdělí na samostatné doklady.",
          en: "From every attachment it reads the vendor, date and amount. A PDF holding several invoices is split into separate documents.",
        },
      },
      {
        title: { cs: "Doklad najde svou platbu", en: "The document finds its payment" },
        text: {
          cs: "Při nejbližším párování Zettl doklad přiřadí k platbě z Fio. Vy jen zkontrolujete.",
          en: "At the next matching run Zettl assigns the document to its Fio payment. You just review.",
        },
      },
    ],
    notes: [
      {
        cs: "Jedna příloha může mít nejvýš 10 MB. AI přečte doklad do 20 stran; větší soubor se uloží, ale nepřečte.",
        en: "One attachment may be up to 10 MB. AI reads documents up to 20 pages; a larger file is stored but not read.",
      },
      {
        cs: "Text e-mailu bez přílohy Zettl nezpracuje: doklad musí být v příloze.",
        en: "Zettl does not process an e-mail's text without an attachment: the document has to be attached.",
      },
      {
        cs: "Soubory ISDOC (XML) e-mailem zatím nepřijímá.",
        en: "It does not accept ISDOC (XML) files by e-mail yet.",
      },
      {
        cs: `Kolik dokladů AI přečte za měsíc, určuje tarif: ${start.name} ${start.limits.aiDocumentsPerMonth}, ${firma.name} ${firma.limits.aiDocumentsPerMonth}. Doklady nad limit se uloží a nepřečtou, nic se neztratí.`,
        en: `Your plan sets how many documents AI reads a month: ${start.name} ${start.limits.aiDocumentsPerMonth}, ${firma.name} ${firma.limits.aiDocumentsPerMonth}. Documents over the limit are stored unread, so nothing is lost.`,
      },
    ],
    faq: [
      {
        id: "adresa",
        question: {
          cs: "Kde najdu e-mailovou adresu pro doklady?",
          en: "Where do I find the address for documents?",
        },
        answer: {
          cs: "V Nastavení → Banky a integrace. Správce firmy ji tam zapne a pak ji vidí všichni členové firmy.",
          en: "Under Settings → Banks & integrations. A company admin switches it on there, and every member of the company can then see it.",
        },
      },
      {
        id: "formaty",
        question: {
          cs: "Jaké soubory můžu poslat?",
          en: "Which files can I send?",
        },
        answer: {
          cs: "PDF, JPG, PNG a WEBP, každý do 10 MB, až 10 příloh v jednom e-mailu. Doklad můžete také nahrát přímo v aplikaci.",
          en: "PDF, JPG, PNG and WEBP, each up to 10 MB, up to 10 attachments per e-mail. You can also upload a document directly in the app.",
        },
      },
      {
        id: "vice-faktur",
        question: {
          cs: "Co když je v jednom PDF víc faktur?",
          en: "What if one PDF holds several invoices?",
        },
        answer: {
          cs: "Zettl je rozdělí na samostatné doklady, každý s vlastním dodavatelem a částkou. Při párování je zkusí přiřadit k jedné platbě společně.",
          en: "Zettl splits them into separate documents, each with its own vendor and amount. When matching, it tries them together against one payment.",
        },
      },
      {
        id: "automaticky",
        question: {
          cs: "Můžu si faktury přeposílat automaticky?",
          en: "Can I forward invoices automatically?",
        },
        answer: {
          cs: "Ano, pokud to váš e-mail umí: pravidlem pro přeposílání ve schránce pošlete faktury od vybraných dodavatelů rovnou na adresu firmy v Zettlu.",
          en: "Yes, if your mailbox supports it: a forwarding rule can send invoices from chosen vendors straight to your company's Zettl address.",
        },
      },
    ],
    related: ["guideMatching", "guideSplitPayment", "guideAccountant"],
  },

  // src/components/receipts/ReceiptsBundleButton.tsx, src/lib/pdf/downloadReceiptsBundle.ts,
  // receiptsBundleSummary.tsx, FioStatementButton, ExportSection (XLSX, ISDOC),
  // TransactionImportExport (payments CSV), OrganizationMembersSection.
  guideAccountant: {
    key: "guideAccountant",
    updated: "2026-11-02",
    seo: {
      title: {
        cs: "Podklady pro účetní za měsíc: doklady v jednom PDF — Zettl",
        en: "Monthly files for your accountant: documents in one PDF — Zettl",
      },
      description: {
        cs: "Zettl ukáže, ke kterým platbám chybí doklad, a za měsíc stáhne všechny účtenky jako jeden PDF s přehledem. Výpis z Fio jako PDF, platby jako CSV.",
        en: "Zettl shows which payments still lack a document and downloads the month's receipts as one PDF with a summary. The Fio statement as PDF, payments as CSV.",
      },
    },
    name: { cs: "Podklady pro účetní za měsíc", en: "Monthly files for your accountant" },
    eyebrow: { cs: "Pro účetní", en: "For accountants" },
    h1: {
      cs: "Podklady pro účetní za měsíc, bez dohledávání",
      en: "Monthly files for your accountant, without the hunt",
    },
    lead: {
      cs: "V Zettlu vidíte, ke kterým platbám z Fio ještě chybí doklad. Když je měsíc hotový, stáhnete všechny účtenky za měsíc jako jeden PDF soubor s přehledem a k němu výpis z Fio. Účetní můžete do firmy rovnou přizvat.",
      en: "Zettl shows which Fio payments still lack a document. Once the month is complete, download all its receipts as one PDF with a summary, plus the Fio statement. Or simply add your accountant to the company.",
    },
    steps: [
      {
        title: { cs: "Zkontrolujte měsíc", en: "Check the month" },
        text: {
          cs: "Přehled měsíce ukáže spárované platby a ty, ke kterým doklad chybí.",
          en: "The month view shows matched payments and the ones still missing a document.",
        },
      },
      {
        title: { cs: "Stáhněte PDF balíček", en: "Download the PDF bundle" },
        text: {
          cs: "Na stránce Účtenky stáhnete za měsíc jeden PDF: přehled a za ním všechny přijaté účtenky a faktury za ten měsíc.",
          en: "On the Receipts page download one PDF for the month: a summary followed by all that month's received receipts and invoices.",
        },
      },
      {
        title: { cs: "Přidejte výpis z Fio", en: "Add the Fio statement" },
        text: {
          cs: "U uzavřeného měsíce stáhnete oficiální PDF výpis z Fio přímo ze Zettlu. Platby za rok vyexportujete jako CSV.",
          en: "For a closed month download Fio's official PDF statement right from Zettl. Export a year of payments as CSV.",
        },
      },
      {
        title: { cs: "Nebo účetní přizvěte", en: "Or invite your accountant" },
        text: {
          cs: "Účetní s vlastním účtem v Zettlu přidáte do firmy a doklady si stáhne sama.",
          en: "Add your accountant with their own Zettl account to the company and let them download the files themselves.",
        },
      },
    ],
    notes: [
      {
        cs: "Přehled v PDF obsahuje počet spárovaných a nespárovaných dokladů, součty podle měny a tabulku dokladů s datem, dodavatelem a částkou.",
        en: "The PDF summary has the count of matched and unmatched documents, totals per currency and a table of documents with date, vendor and amount.",
      },
      {
        cs: "PDF s několika fakturami (třeba objednávka s více prodejci) se do balíčku přiloží jen jednou; přehled vypíše každou fakturu.",
        en: "A PDF holding several invoices (an order with several sellers, say) is attached to the bundle once; the summary lists every invoice.",
      },
      {
        cs: "Balíček obsahuje přijaté doklady. Vydané faktury vyexportujete zvlášť do XLSX nebo ISDOC.",
        en: "The bundle holds received documents. Export issued invoices separately to XLSX or ISDOC.",
      },
      {
        cs: "Ke stažení PDF výpisu musí mít Fio token oprávnění ke stahování výpisů. Fio výpis vytvoří až po skončení měsíce.",
        en: "To download the PDF statement, the Fio token needs the statement download permission. Fio creates the statement only after the month ends.",
      },
      {
        cs: `Tarif ${start.name} má ${start.limits.users} uživatele, tedy vás a účetní.`,
        en: `The ${start.name} plan has ${start.limits.users} users: you and your accountant.`,
      },
    ],
    faq: [
      {
        id: "co-obsahuje",
        question: {
          cs: "Co přesně je v měsíčním PDF balíčku?",
          en: "What exactly is in the monthly PDF bundle?",
        },
        answer: {
          cs: "Na začátku přehled: kolik dokladů je spárovaných a nespárovaných, součty podle měny a seznam dokladů. Za ním následují samotné doklady seřazené podle data, PDF stránky i fotky účtenek.",
          en: "First a summary: how many documents are matched and unmatched, totals per currency and a list of documents. Then the documents themselves in date order, PDF pages and receipt photos alike.",
        },
      },
      {
        id: "export",
        question: {
          cs: "V jakých formátech dostanu data pro účetní?",
          en: "Which formats can I give my accountant?",
        },
        answer: {
          cs: "Doklady za měsíc jako jeden PDF, výpis z Fio jako PDF, platby za rok jako CSV a vydané faktury jako XLSX nebo ISDOC.",
          en: "The month's documents as one PDF, the Fio statement as PDF, a year of payments as CSV, and issued invoices as XLSX or ISDOC.",
        },
      },
      {
        id: "pristup-ucetni",
        question: {
          cs: "Může účetní pracovat přímo v Zettlu?",
          en: "Can my accountant work directly in Zettl?",
        },
        answer: {
          cs: "Ano. Pozvete ji do firmy a jako členka týmu vidí platby i doklady a může je upravovat.",
          en: "Yes. Invite them to the company; as a team member they see payments and documents and can edit them.",
        },
      },
      {
        id: "ucetnictvi",
        question: {
          cs: "Vede Zettl účetnictví?",
          en: "Does Zettl do the bookkeeping?",
        },
        answer: {
          cs: "Ne. Zettl připraví spárované platby a doklady; zaúčtování a daňová přiznání dělá vaše účetní ve svém programu.",
          en: "No. Zettl prepares matched payments and documents; your accountant books them and files the tax returns in their own software.",
        },
      },
    ],
    related: ["guideMatching", "guideIsdoc", "guideVsSpreadsheet"],
  },

  // src/lib/exports/isdoc.ts (6.0.2), exportToISDOC.ts (issued only, .isdoc or .zip,
  // ČNB rate via cnb-rate), Webfaktura ISDOC button; invoicing is a Firma feature.
  guideIsdoc: {
    key: "guideIsdoc",
    updated: "2026-11-03",
    seo: {
      title: {
        cs: "ISDOC export faktur pro účetní — Zettl",
        en: "ISDOC invoice export for your accountant — Zettl",
      },
      description: {
        cs: "Faktury vystavené v Zettlu vyexportujete do ISDOC 6.0.2: jednu jako soubor .isdoc, více najednou jako ZIP. Faktury v cizí měně dostanou kurz ČNB.",
        en: "Export invoices issued in Zettl to ISDOC 6.0.2: one as an .isdoc file, several at once as a ZIP. Foreign-currency invoices get the ČNB rate.",
      },
    },
    name: { cs: "ISDOC export faktur", en: "ISDOC invoice export" },
    eyebrow: { cs: "Fakturace", en: "Invoicing" },
    h1: {
      cs: "Export vydaných faktur do ISDOC",
      en: "Export issued invoices to ISDOC",
    },
    lead: {
      cs: "ISDOC je český standard elektronické faktury, který načte většina účetních programů. Faktury vystavené v Zettlu do něj vyexportujete jedním klikem: jednu jako soubor .isdoc, více najednou jako ZIP.",
      en: "ISDOC is the Czech e-invoice standard most accounting software can read. Export invoices issued in Zettl to it in one click: one as an .isdoc file, several at once as a ZIP.",
    },
    steps: [
      {
        title: { cs: "Vystavte fakturu", en: "Issue the invoice" },
        text: {
          cs: "Fakturu vystavíte v Zettlu se šablonou, QR platbou a odběratelem načteným z ARES.",
          en: "Issue the invoice in Zettl with a template, a QR payment code and the customer looked up in ARES.",
        },
      },
      {
        title: { cs: "Vyberte faktury", en: "Pick the invoices" },
        text: {
          cs: "V exportu na stránce Faktury vyfiltrujete faktury podle stavu a data vystavení.",
          en: "In the export on the Invoices page, filter invoices by status and issue date.",
        },
      },
      {
        title: { cs: "Stáhněte ISDOC", en: "Download ISDOC" },
        text: {
          cs: "Jedna faktura se stáhne jako .isdoc, více faktur jako ZIP s jedním souborem na fakturu.",
          en: "One invoice downloads as .isdoc, several as a ZIP with one file per invoice.",
        },
      },
      {
        title: { cs: "Pošlete účetní", en: "Send it to your accountant" },
        text: {
          cs: "Účetní soubory načte do svého programu bez přepisování.",
          en: "Your accountant loads the files into their software without retyping.",
        },
      },
    ],
    notes: [
      {
        cs: "Export odpovídá ISDOC verze 6.0.2.",
        en: "The export follows ISDOC version 6.0.2.",
      },
      {
        cs: "Exportují se vydané faktury a zálohové faktury. Dobropisy do ISDOC Zettl zatím neumí.",
        en: "Issued invoices and proforma invoices are exported. Zettl cannot export credit notes to ISDOC yet.",
      },
      {
        cs: "Faktura bez odběratele nebo bez položek se do ISDOC nevyexportuje; Zettl vám řekne, která to je.",
        en: "An invoice without a customer or without items is not exported to ISDOC; Zettl tells you which one.",
      },
      {
        cs: "Vydané faktury z jiného programu nebo ze Shoptetu (ZIP) do Zettlu naimportujete ze souborů ISDOC, s náhledem a přeskočením faktur, které už v Zettlu jsou.",
        en: "Issued invoices from other software or from Shoptet (a ZIP) can be imported into Zettl from ISDOC files, with a preview and invoices already in Zettl skipped.",
      },
      {
        cs: `Vystavování faktur je součástí tarifu ${firma.name} (${formatCzk(firma.priceMonthlyCzk)} měsíčně).`,
        en: `Invoicing is part of the ${firma.name} plan (${formatCzk(firma.priceMonthlyCzk)} a month).`,
      },
    ],
    faq: [
      {
        id: "co-je-isdoc",
        question: {
          cs: "Co je ISDOC?",
          en: "What is ISDOC?",
        },
        answer: {
          cs: "Český formát elektronické faktury v XML. Účetní program z něj načte odběratele, položky, DPH i částky, takže fakturu nikdo nepřepisuje.",
          en: "The Czech e-invoice format in XML. Accounting software reads the customer, items, VAT and amounts from it, so nobody retypes the invoice.",
        },
      },
      {
        id: "cizi-mena",
        question: {
          cs: "Jak ISDOC export řeší faktury v cizí měně?",
          en: "How does the ISDOC export handle foreign-currency invoices?",
        },
        answer: {
          cs: "Uvede částky v cizí měně i v korunách a kurz ČNB ke dni zdanitelného plnění, a když ho faktura nemá, ke dni vystavení. Pokud kurz nejde načíst, Zettl takovou fakturu nevyexportuje a řekne vám to.",
          en: "It gives the amounts in the foreign currency and in CZK, with the ČNB rate for the taxable supply date, or the issue date when the invoice has none. If the rate cannot be loaded, Zettl does not export that invoice and tells you so.",
        },
      },
      {
        id: "prijate",
        question: {
          cs: "Vyexportuji do ISDOC i přijaté faktury?",
          en: "Can I export received invoices to ISDOC too?",
        },
        answer: {
          cs: "Ne, ISDOC export je pro faktury vystavené v Zettlu. Přijaté doklady stáhnete za měsíc jako jeden PDF.",
          en: "No, the ISDOC export is for invoices issued in Zettl. Download received documents for a month as one PDF.",
        },
      },
    ],
    related: ["toolIsdocViewer", "guideForeignCurrency", "guideAccountant"],
  },

  // InvoiceForm currencies (CZK, EUR, USD, GBP), exportToISDOC.ts rateFor
  // (taxable_date || issue_date), supabase/functions/cnb-rate (ČNB daily rates API).
  guideForeignCurrency: {
    key: "guideForeignCurrency",
    updated: "2026-11-02",
    seo: {
      title: {
        cs: "Faktura v cizí měně a kurz ČNB v ISDOC — Zettl",
        en: "Foreign-currency invoices and the ČNB rate in ISDOC — Zettl",
      },
      description: {
        cs: "V Zettlu vystavíte fakturu v EUR, USD nebo GBP. Při exportu do ISDOC doplní kurz ČNB ke dni zdanitelného plnění a částky v korunách.",
        en: "Issue an invoice in EUR, USD or GBP in Zettl. The ISDOC export adds the ČNB rate for the taxable supply date and the amounts in CZK.",
      },
    },
    name: { cs: "Faktura v cizí měně a kurz ČNB", en: "Foreign-currency invoice and ČNB rate" },
    eyebrow: { cs: "Cizí měny", en: "Foreign currencies" },
    h1: {
      cs: "Faktura v cizí měně a kurz ČNB",
      en: "Foreign-currency invoices and the ČNB rate",
    },
    lead: {
      cs: "Fakturu v Zettlu vystavíte v korunách, eurech, dolarech nebo librách. Když ji exportujete do ISDOC, Zettl načte denní kurz ČNB ke dni zdanitelného plnění a uvede částky v cizí měně i v korunách.",
      en: "Issue an invoice in Zettl in CZK, EUR, USD or GBP. When you export it to ISDOC, Zettl loads the ČNB daily rate for the taxable supply date and gives the amounts in the foreign currency and in CZK.",
    },
    steps: [
      {
        title: { cs: "Vystavte fakturu v měně", en: "Issue the invoice in its currency" },
        text: {
          cs: "Ve formuláři faktury zvolíte měnu: CZK, EUR, USD nebo GBP.",
          en: "In the invoice form choose the currency: CZK, EUR, USD or GBP.",
        },
      },
      {
        title: { cs: "Exportujte do ISDOC", en: "Export to ISDOC" },
        text: {
          cs: "Zettl si pro každou fakturu v cizí měně vyžádá kurz z ČNB ke dni zdanitelného plnění, nebo ke dni vystavení, když faktura DUZP nemá.",
          en: "For each foreign-currency invoice Zettl requests the ČNB rate for the taxable supply date, or the issue date when the invoice has none.",
        },
      },
      {
        title: { cs: "Částky v obou měnách", en: "Amounts in both currencies" },
        text: {
          cs: "V ISDOC je cizí měna, kurz a každá částka v cizí měně i přepočtená na koruny.",
          en: "The ISDOC carries the foreign currency, the rate and every amount both in that currency and converted to CZK.",
        },
      },
      {
        title: { cs: "Platba v cizí měně", en: "Payment in that currency" },
        text: {
          cs: "Eurový účet u Fio připojíte zvlášť a Zettl páruje platby v jeho měně.",
          en: "Connect your Fio EUR account separately and Zettl matches payments in its currency.",
        },
      },
    ],
    notes: [
      {
        cs: "O víkendu a ve svátek ČNB nový kurz nevyhlašuje; platí poslední vyhlášený, a ten Zettl použije.",
        en: "ČNB publishes no new rate on weekends and holidays; the last published rate applies, and Zettl uses it.",
      },
      {
        cs: "Faktury vystavené v Zettlu zatím nemají pole pro DUZP, takže kurz se pro ně bere ke dni vystavení. Faktury převedené z Fakturoidu DUZP mají.",
        en: "Invoices issued in Zettl have no taxable-supply-date field yet, so their rate is taken for the issue date. Invoices moved over from Fakturoid do carry the date.",
      },
      {
        cs: "Kurz Zettl použije v ISDOC exportu. PDF faktury ukazuje částky jen v měně faktury, bez přepočtu na koruny.",
        en: "Zettl uses the rate in the ISDOC export. The invoice PDF shows amounts only in the invoice currency, without a CZK conversion.",
      },
      {
        cs: `Vystavování faktur je součástí tarifu ${firma.name}.`,
        en: `Invoicing is part of the ${firma.name} plan.`,
      },
    ],
    faq: [
      {
        id: "ktery-kurz",
        question: {
          cs: "Jaký kurz ČNB se na fakturu v cizí měně použije?",
          en: "Which ČNB rate applies to a foreign-currency invoice?",
        },
        answer: {
          cs: "Zettl v ISDOC exportu použije denní kurz ČNB ke dni zdanitelného plnění, a když faktura DUZP nemá, ke dni vystavení. Na den bez vyhlášeného kurzu platí poslední vyhlášený.",
          en: "In the ISDOC export Zettl uses the ČNB daily rate for the taxable supply date, or the issue date when the invoice has none. On a day with no published rate, the last published rate applies.",
        },
      },
      {
        id: "meny",
        question: {
          cs: "V jakých měnách můžu fakturovat?",
          en: "Which currencies can I invoice in?",
        },
        answer: {
          cs: "V korunách, eurech, amerických dolarech a britských librách.",
          en: "Czech crowns, euros, US dollars and British pounds.",
        },
      },
      {
        id: "kurz-na-fakture",
        question: {
          cs: "Ukáže faktura v PDF přepočet na koruny?",
          en: "Does the invoice PDF show the CZK equivalent?",
        },
        answer: {
          cs: "Zatím ne. PDF ukazuje částky v měně faktury; kurz ČNB a částky v korunách jsou v ISDOC exportu.",
          en: "Not yet. The PDF shows amounts in the invoice currency; the ČNB rate and the CZK amounts are in the ISDOC export.",
        },
      },
    ],
    related: ["guideIsdoc", "guideAccountant", "guideFioApi"],
  },

  // _shared/sum-match.ts (exact sum, same currency, 7 days, max 6 documents),
  // _shared/multi-document.ts, ReceiptSetPicker / SelectionSum, TransactionSetPicker (±45 days).
  guideSplitPayment: {
    key: "guideSplitPayment",
    updated: "2026-11-02",
    seo: {
      title: {
        cs: "Jedna platba, více faktur: párování objednávek — Zettl",
        en: "One payment, several invoices: matching split orders — Zettl",
      },
      description: {
        cs: "Objednávka z e-shopu nebo marketplace s fakturami od více prodejců a jedna platba kartou? Zettl spáruje součet faktur s platbou automaticky.",
        en: "A web-shop or marketplace order with invoices from several sellers and one card payment? Zettl matches the sum of the invoices with the payment automatically.",
      },
    },
    name: { cs: "Jedna platba, více faktur", en: "One payment, several invoices" },
    eyebrow: { cs: "Párování", en: "Matching" },
    h1: {
      cs: "Jedna platba, více faktur: Zettl je spáruje dohromady",
      en: "One payment, several invoices: Zettl matches them together",
    },
    lead: {
      cs: "Objednávka na marketplace (třeba Amazon) často přijde jako několik faktur od různých prodejců, ale z účtu odejde jedna platba. Zettl takové faktury pozná a spáruje je s platbou jako skupinu, když jejich součet přesně sedí.",
      en: "A marketplace order (Amazon, say) often comes as several invoices from different sellers, while one payment leaves your account. Zettl recognises such invoices and matches them with the payment as a group when their sum agrees exactly.",
    },
    steps: [
      {
        title: { cs: "PDF se rozdělí", en: "The PDF is split" },
        text: {
          cs: "Když jedno PDF obsahuje víc faktur, AI je rozdělí na samostatné doklady a ty zůstanou svázané jako jedna skupina.",
          en: "When one PDF holds several invoices, AI splits them into separate documents that stay linked as one group.",
        },
      },
      {
        title: { cs: "Součet proti platbě", en: "The sum against the payment" },
        text: {
          cs: "Zettl hledá doklady, jejichž součet sedí s platbou na haléř, ve stejné měně a do 7 dní. Nejdřív zkusí celou skupinu z jednoho PDF.",
          en: "Zettl looks for documents whose sum matches the payment to the cent, in the same currency and within 7 days. It tries the whole group from one PDF first.",
        },
      },
      {
        title: { cs: "Jen jednoznačná shoda", en: "Only an unambiguous fit" },
        text: {
          cs: "Automaticky spáruje jen tehdy, když se hodí právě jedna kombinace dokladů. Jinak nechá rozhodnutí na vás.",
          en: "It matches automatically only when exactly one combination of documents fits. Otherwise it leaves the call to you.",
        },
      },
      {
        title: { cs: "Ručně s kontrolou součtu", en: "By hand, with a running sum" },
        text: {
          cs: "K platbě vyberete víc dokladů a Zettl ukazuje, jestli součet sedí, nebo kolik chybí či přebývá.",
          en: "Pick several documents for a payment and Zettl shows whether the sum fits, or how much is missing or extra.",
        },
      },
    ],
    notes: [
      {
        cs: "Automaticky Zettl spáruje s jednou platbou nejvýš 6 dokladů.",
        en: "Automatically, Zettl matches at most 6 documents with one payment.",
      },
      {
        cs: "Opačný případ, jeden doklad zaplacený několika platbami (třeba ve splátkách), spárujete ručně: Zettl nabídne platby do 45 dní od data dokladu a předvybere ty, jejichž součet sedí.",
        en: "The reverse, one document paid in several payments (instalments, say), you match by hand: Zettl offers payments within 45 days of the document date and pre-selects the ones whose sum fits.",
      },
      {
        cs: "Zrušením se zruší celá skupina najednou.",
        en: "Undoing removes the whole group at once.",
      },
      {
        cs: "V měsíčním PDF balíčku se sdílené PDF přiloží jen jednou, přehled vypíše každou fakturu.",
        en: "In the monthly PDF bundle a shared PDF is attached once; the summary lists every invoice.",
      },
    ],
    faq: [
      {
        id: "amazon",
        question: {
          cs: "Jak spárovat objednávku z Amazonu rozdělenou do více faktur?",
          en: "How do I match an Amazon order split into several invoices?",
        },
        answer: {
          cs: "Přepošlete faktury do Zettlu. Když jejich součet přesně odpovídá jedné platbě kartou ve stejné měně a do 7 dní, Zettl je s ní spáruje sám jako skupinu. Jinak je k platbě vyberete ručně a Zettl ukáže, jestli součet sedí.",
          en: "Forward the invoices to Zettl. When their sum matches one card payment exactly, in the same currency and within 7 days, Zettl matches them as a group on its own. Otherwise pick them for the payment by hand and Zettl shows whether the sum fits.",
        },
      },
      {
        id: "splatky",
        question: {
          cs: "Co když jednu fakturu platím na několikrát?",
          en: "What if I pay one invoice in several payments?",
        },
        answer: {
          cs: "Takové párování uděláte ručně: k dokladu vyberete několik plateb. Zettl nabídne platby do 45 dní od data dokladu a předvybere kombinaci, která sedí.",
          en: "You match that by hand: pick several payments for the document. Zettl offers payments within 45 days of the document date and pre-selects the combination that fits.",
        },
      },
      {
        id: "nesedi",
        question: {
          cs: "Co když součet faktur nesedí na haléř?",
          en: "What if the invoices don't add up to the cent?",
        },
        answer: {
          cs: "Pravidlo pro součet čeká přesnou shodu. Platbu může ještě spárovat AI, když rozdíl nepřesáhne 5 %, a jinak ji spárujete ručně; Zettl při výběru ukáže, kolik chybí nebo přebývá.",
          en: "The sum rule expects an exact match. AI may still match the payment when the difference is within 5%; otherwise you match it by hand, and Zettl shows how much is missing or extra while you pick.",
        },
      },
    ],
    related: ["guideMatching", "guideEmailInbox", "guideAccountant"],
  },

  // supabase/functions/mcp (tools.ts: 10 read, 4 write tools behind zettl:write),
  // mcp-oauth (OAuth 2.1), McpConnectionsSection; feature:mcpConnector in Firma.
  guideMcp: {
    key: "guideMcp",
    updated: "2026-11-02",
    seo: {
      title: {
        cs: "AI asistent nad vaším účetnictvím přes MCP (Claude) — Zettl",
        en: "An AI assistant over your own books via MCP (Claude) — Zettl",
      },
      description: {
        cs: "Připojte Zettl do Claude nebo jiného AI asistenta přes MCP. Zeptejte se, které platby nemají doklad, nebo nechte asistenta doklad spárovat.",
        en: "Connect Zettl to Claude or another AI assistant via MCP. Ask which payments lack a document, or let the assistant match one.",
      },
    },
    name: { cs: "AI asistent přes MCP", en: "AI assistant via MCP" },
    eyebrow: { cs: "AI asistent", en: "AI assistant" },
    h1: {
      cs: "AI asistent nad vašimi platbami a doklady, přes MCP",
      en: "An AI assistant over your payments and documents, via MCP",
    },
    lead: {
      cs: "Zettl je vzdálený MCP server na adrese https://zettlapp.com/mcp. Přidáte ho do AI asistenta s podporou MCP, třeba Claude, a ptáte se vlastními slovy: které platby nemají doklad, kolik jste utratili, které faktury nejsou zaplacené.",
      en: "Zettl is a remote MCP server at https://zettlapp.com/mcp. Add it to an MCP-capable AI assistant such as Claude and ask in your own words: which payments lack a document, what you spent, which invoices are unpaid.",
    },
    steps: [
      {
        title: { cs: "Přidejte konektor", en: "Add the connector" },
        text: {
          cs: "V asistentovi přidáte vlastní konektor s adresou https://zettlapp.com/mcp.",
          en: "In the assistant, add a custom connector with the address https://zettlapp.com/mcp.",
        },
      },
      {
        title: { cs: "Přihlaste se a povolte", en: "Sign in and allow" },
        text: {
          cs: "Přihlásíte se účtem Zettl (OAuth 2.1) a povolíte přístup k jedné firmě: jen ke čtení, nebo i ke změnám.",
          en: "Sign in with your Zettl account (OAuth 2.1) and allow access to one company: read only, or changes too.",
        },
      },
      {
        title: { cs: "Ptejte se", en: "Ask" },
        text: {
          cs: "Asistent čte faktury, platby, doklady, klienty a připojené účty, najde platby bez dokladu a shrne příjmy a výdaje.",
          en: "The assistant reads invoices, payments, documents, clients and connected accounts, finds payments without a document and summarizes income and expenses.",
        },
      },
      {
        title: { cs: "Nechte ho pracovat", en: "Let it work" },
        text: {
          cs: "S povolenými změnami umí spárovat doklad s platbou, párování zrušit, nastavit kategorii platby a změnit stav faktury.",
          en: "With changes allowed it can match a document to a payment, undo a match, set a payment's category and change an invoice's status.",
        },
      },
    ],
    notes: [
      {
        cs: "Asistent jedná s vašimi právy a vidí jen firmu, ke které jste přístup povolili.",
        en: "The assistant acts with your permissions and sees only the company you granted.",
      },
      {
        cs: "Přihlašovací údaje k bance ani API token asistent nikdy nevidí.",
        en: "The assistant never sees bank credentials or the API token.",
      },
      {
        cs: "Připojení zrušíte v Nastavení → Banky a integrace → AI připojení; zrušení platí okamžitě.",
        en: "Revoke a connection under Settings → Banks & integrations → AI connections; it takes effect immediately.",
      },
      {
        cs: `MCP konektor je součástí tarifu ${firma.name} (${formatCzk(firma.priceMonthlyCzk)} měsíčně).`,
        en: `The MCP connector is part of the ${firma.name} plan (${formatCzk(firma.priceMonthlyCzk)} a month).`,
      },
    ],
    faq: [
      {
        id: "co-je-mcp",
        question: {
          cs: "Co je MCP?",
          en: "What is MCP?",
        },
        answer: {
          cs: "Model Context Protocol, otevřený standard, přes který AI asistent bezpečně pracuje s daty jiné aplikace. Zettl ho nabízí jako vzdálený server, nic neinstalujete.",
          en: "The Model Context Protocol, an open standard through which an AI assistant works with another app's data. Zettl offers it as a remote server, so there is nothing to install.",
        },
      },
      {
        id: "ktere-asistenty",
        question: {
          cs: "Se kterými asistenty to funguje?",
          en: "Which assistants does it work with?",
        },
        answer: {
          cs: "S asistenty, které umí připojit vzdálený MCP server s přihlášením přes OAuth, například Claude.",
          en: "With assistants that can connect a remote MCP server with OAuth sign-in, such as Claude.",
        },
      },
      {
        id: "zmeny",
        question: {
          cs: "Může asistent něco změnit bez mého svolení?",
          en: "Can the assistant change anything without my permission?",
        },
        answer: {
          cs: "Jen pokud při připojení zaškrtnete „Povolit i změny dat“. Bez toho je připojení jen pro čtení a nástroje pro změny asistent vůbec nevidí.",
          en: "Only if you tick “Also allow changes” when connecting. Without it the connection is read-only and the assistant does not even see the tools for changes.",
        },
      },
    ],
    related: ["guideMatching", "guideAccountant", "guideFioApi"],
  },

  // Comparison against doing it by hand; facts from the guides above.
  guideVsSpreadsheet: {
    key: "guideVsSpreadsheet",
    updated: "2026-11-03",
    seo: {
      title: {
        cs: "Zettl, nebo párování dokladů v tabulce? Poctivé srovnání",
        en: "Zettl or matching documents in a spreadsheet? An honest comparison",
      },
      description: {
        cs: "Kdy stačí Excel a ruční párování plateb s doklady a kdy se vyplatí Zettl. Co Zettl udělá za vás a co neumí.",
        en: "When a spreadsheet and matching by hand are enough, and when Zettl pays off. What Zettl does for you and what it doesn't.",
      },
    },
    name: { cs: "Zettl, nebo tabulka?", en: "Zettl or a spreadsheet?" },
    eyebrow: { cs: "Srovnání", en: "Comparison" },
    h1: {
      cs: "Zettl, nebo párování dokladů ručně v tabulce?",
      en: "Zettl, or matching documents by hand in a spreadsheet?",
    },
    lead: {
      cs: "S pár platbami měsíčně tabulka stačí. Čím víc plateb a dokladů, tím víc času stojí ruční přepisování výpisu a dohledávání účtenek. Zettl tu práci dělá za vás, ale jen pro účet u Fio banky a bez vedení účetnictví.",
      en: "With a handful of payments a month a spreadsheet will do. The more payments and documents, the more time copying statements and chasing receipts takes. Zettl does that work for you, but only for a Fio banka account and without doing the bookkeeping.",
    },
    steps: [
      {
        title: { cs: "Ručně: stáhnout výpis", en: "By hand: download the statement" },
        text: {
          cs: "Každý měsíc výpis z banky, import nebo přepis do tabulky. Se Zettlem přijdou platby z Fio samy každé ráno.",
          en: "Every month a bank statement, imported or retyped into a spreadsheet. With Zettl, Fio payments arrive on their own every morning.",
        },
      },
      {
        title: { cs: "Ručně: posbírat doklady", en: "By hand: collect documents" },
        text: {
          cs: "Faktury z e-mailů ukládat do složky. Se Zettlem je přepošlete na adresu firmy a AI je přečte.",
          en: "Save invoices from e-mails into a folder. With Zettl, forward them to your company address and AI reads them.",
        },
      },
      {
        title: { cs: "Ručně: spárovat", en: "By hand: match" },
        text: {
          cs: "Řádek po řádku hledat, k čemu která platba patří. Zettl spáruje sám a vy zkontrolujete.",
          en: "Row by row, work out what each payment belongs to. Zettl matches on its own and you review.",
        },
      },
      {
        title: { cs: "Ručně: poslat účetní", en: "By hand: send to the accountant" },
        text: {
          cs: "Zabalit doklady a tabulku. Zettl stáhne doklady za měsíc jako jeden PDF s přehledem.",
          en: "Bundle the documents and the sheet. Zettl downloads the month's documents as one PDF with a summary.",
        },
      },
    ],
    comparison: [
      {
        task: { cs: "Platby z banky", en: "Bank payments" },
        byHand: {
          cs: "Stáhnout výpis a vložit nebo přepsat do tabulky",
          en: "Download the statement and paste or retype it",
        },
        zettl: {
          cs: "Každé ráno z Fio API; starší období jako GPC/ABO",
          en: "Every morning from the Fio API; older periods as GPC/ABO",
        },
      },
      {
        task: { cs: "Údaje z dokladu", en: "Details from a document" },
        byHand: {
          cs: "Opsat dodavatele, datum a částku",
          en: "Copy vendor, date and amount",
        },
        zettl: { cs: "AI je přečte z PDF i fotky", en: "AI reads them from PDFs and photos" },
      },
      {
        task: { cs: "Párování", en: "Matching" },
        byHand: { cs: "Ručně, řádek po řádku", en: "By hand, row by row" },
        zettl: {
          cs: "Automaticky, se skóre shody; zrušení jedním klikem",
          en: "Automatic, with a score; undo in one click",
        },
      },
      {
        task: { cs: "Jedna platba, více faktur", en: "One payment, several invoices" },
        byHand: { cs: "Sčítat na kalkulačce", en: "Add them up on a calculator" },
        zettl: {
          cs: "Najde přesný součet až 6 dokladů",
          en: "Finds the exact sum of up to 6 documents",
        },
      },
      {
        task: { cs: "Co chybí", en: "What's missing" },
        byHand: { cs: "Filtr nebo barvičky v tabulce", en: "A filter or colours in the sheet" },
        zettl: {
          cs: "Nespárované platby v přehledu měsíce",
          en: "Unmatched payments in the month view",
        },
      },
      {
        task: { cs: "Podklady pro účetní", en: "Files for the accountant" },
        byHand: { cs: "Složka s doklady a tabulka", en: "A folder of documents and the sheet" },
        zettl: {
          cs: "Jeden PDF za měsíc s přehledem, výpis z Fio, CSV plateb",
          en: "One PDF a month with a summary, the Fio statement, a payments CSV",
        },
      },
      {
        task: { cs: "Cena", en: "Price" },
        byHand: { cs: "Váš čas", en: "Your time" },
        zettl: {
          cs: `Od ${formatCzk(start.priceMonthlyCzk)} měsíčně, prvních ${trialDays} dní zdarma`,
          en: `From ${formatCzk(start.priceMonthlyCzk)} a month, first ${trialDays} days free`,
        },
      },
    ],
    notes: [
      {
        cs: "Tabulka vám stačí, když máte jen pár plateb měsíčně, nebo účet u jiné banky než Fio: Zettl jiné banky nepodporuje.",
        en: "A spreadsheet will do if you have only a few payments a month, or an account at a bank other than Fio: Zettl does not support other banks.",
      },
      {
        cs: "Zettl nevede účetnictví a nepodává přiznání. Připraví spárované platby a doklady pro vaši účetní.",
        en: "Zettl does not do the bookkeeping or file tax returns. It prepares matched payments and documents for your accountant.",
      },
      {
        cs: "Automatické párování se může splést. Proto má každé skóre a jde zrušit; kontrolu nechává na vás.",
        en: "Automatic matching can get it wrong. That is why every match has a score and can be undone; the final check is yours.",
      },
      {
        cs: "Data ze Zettlu nezůstanou zamčená: platby vyexportujete jako CSV a doklady jako PDF.",
        en: "Your data doesn't stay locked in: export payments as CSV and documents as PDF.",
      },
    ],
    faq: [
      {
        id: "kdy-tabulka",
        question: {
          cs: "Kdy se Zettl nevyplatí?",
          en: "When is Zettl not worth it?",
        },
        answer: {
          cs: "Když máte za měsíc jen pár plateb, které spárujete za pár minut, nebo když nemáte účet u Fio banky. Zettl umí stahovat platby jen z Fio.",
          en: "When you have only a few payments a month that you match in minutes, or when you don't bank with Fio banka. Zettl can only download payments from Fio.",
        },
      },
      {
        id: "excel-export",
        question: {
          cs: "Dostanu data ze Zettlu zpátky do Excelu?",
          en: "Can I get my data back into Excel?",
        },
        answer: {
          cs: "Ano. Platby za rok vyexportujete jako CSV a vydané faktury jako XLSX. Doklady stáhnete za měsíc jako jeden PDF.",
          en: "Yes. Export a year of payments as CSV and issued invoices as XLSX. Download a month's documents as one PDF.",
        },
      },
      {
        id: "zkusit",
        question: {
          cs: "Můžu Zettl vyzkoušet bez závazku?",
          en: "Can I try Zettl without commitment?",
        },
        answer: {
          cs: `Ano. Prvních ${trialDays} dní je zdarma a platební kartu nezadáváte. Pak ${start.name} stojí ${formatCzk(start.priceMonthlyCzk)} a ${firma.name} ${formatCzk(firma.priceMonthlyCzk)} měsíčně; nejsme plátci DPH, cena je konečná.`,
          en: `Yes. The first ${trialDays} days are free and you enter no card. After that ${start.name} costs ${formatCzk(start.priceMonthlyCzk)} and ${firma.name} ${formatCzk(firma.priceMonthlyCzk)} a month; we are not VAT registered, so the price is final.`,
        },
      },
    ],
    related: ["guideMatching", "guideAccountant", "guideFioApi"],
  },

  // Fakturoid, from its own public pages only (checked 2026-11-03, URLs in `sources`):
  // tagline (fakturoid.cz), "Ceny s DPH", 30 days free, free plan up to 5 customers (/cenik),
  // bank pairing from the bank's e-mail notifications, Fio supported, statement upload not
  // supported, incoming payments to invoices and outgoing to costs (parovani-plateb-s-bankou),
  // Krabice na náklady by e-mail with AI extraction (/naklady), export to accounting programs
  // incl. Pohoda (/cenik, /pro-ucetni). No paid prices: the page's figures are not labelled
  // clearly enough to quote. Owner approved naming it (orchestrator, 2026-11-03); § 2980 OZ.
  guideFakturoid: {
    key: "guideFakturoid",
    updated: "2026-11-03",
    seo: {
      title: {
        cs: "Zettl a Fakturoid: fakturace, nebo párování plateb z Fio? — Zettl",
        en: "Zettl and Fakturoid: invoicing, or matching Fio payments? — Zettl",
      },
      description: {
        cs: "Fakturoid je postavený na fakturaci, Zettl na stahování plateb z Fio přes API a jejich párování s doklady. Kdy se hodí který a jak fungují spolu.",
        en: "Fakturoid is built around invoicing, Zettl around downloading Fio payments through the API and matching them with documents. When each fits, and how they work together.",
      },
    },
    name: { cs: "Zettl a Fakturoid", en: "Zettl and Fakturoid" },
    eyebrow: { cs: "Srovnání", en: "Comparison" },
    h1: {
      cs: "Zettl a Fakturoid: fakturace, nebo párování plateb z Fio?",
      en: "Zettl and Fakturoid: invoicing, or matching Fio payments?",
    },
    lead: {
      cs: "Fakturoid je především fakturační služba: faktury, upomínky a párování příchozích plateb. Zettl je postavený na tom, aby každý den stáhl platby z vašeho účtu u Fio přes API a k odchozím platbám hledal účtenky a faktury. Kdo hlavně fakturuje, sáhne spíš po Fakturoidu; kdo potřebuje mít doklady ke každé platbě z Fio, po Zettlu. Používat se dají i spolu.",
      en: "Fakturoid is above all an invoicing service: invoices, reminders and pairing incoming payments. Zettl is built to download the payments from your Fio account through the API every day and look for receipts and invoices for the outgoing ones. If invoicing is your main job, Fakturoid is the more natural fit; if you need a document for every Fio payment, Zettl is. They also work side by side.",
    },
    comparisonWith: {
      name: "Fakturoid",
      title: { cs: "Fakturoid a Zettl vedle sebe", en: "Fakturoid and Zettl side by side" },
    },
    comparison: [
      {
        task: { cs: "Hlavní úkol", en: "Main job" },
        byHand: {
          cs: "Fakturace: faktury s QR kódem, upomínky, párování plateb",
          en: "Invoicing: invoices with a QR code, reminders, payment pairing",
        },
        zettl: {
          cs: "Platby z Fio a doklady k nim: párování, co chybí, podklady pro účetní",
          en: "Fio payments and their documents: matching, what's missing, files for the accountant",
        },
      },
      {
        task: { cs: "Jak přijdou platby", en: "How payments arrive" },
        byHand: {
          cs: "Z e-mailových oznámení, která posílá banka; podporuje víc českých bank včetně Fio",
          en: "From the e-mail notifications your bank sends; supports several Czech banks, Fio included",
        },
        zettl: {
          cs: "Přes Fio API každý den; jen Fio banka",
          en: "Through the Fio API every day; Fio banka only",
        },
      },
      {
        task: { cs: "Starší výpisy", en: "Older statements" },
        byHand: {
          cs: "Nahrávání bankovních výpisů nepodporuje",
          en: "Does not support uploading bank statements",
        },
        zettl: {
          cs: "Výpis GPC/ABO nebo CSV, bez duplicit s Fio API",
          en: "GPC/ABO or CSV statement, no duplicates with the Fio API",
        },
      },
      {
        task: { cs: "Přijaté doklady", en: "Received documents" },
        byHand: {
          cs: "Krabice na náklady: doklad pošlete e-mailem, vytěžení AI je placená služba",
          en: "Krabice na náklady: send the document by e-mail; AI extraction is a paid service",
        },
        zettl: {
          cs: "Adresa firmy @zettl.cz; čtení AI je v tarifu do měsíčního limitu",
          en: "The company's @zettl.cz address; AI reading is in the plan up to a monthly limit",
        },
      },
      {
        task: { cs: "Pro účetní", en: "For the accountant" },
        byHand: {
          cs: "Pozvání účetní; export do několika účetních programů včetně Pohody",
          en: "Invite the accountant; export to several accounting programs including Pohoda",
        },
        zettl: {
          cs: "Pozvání účetní; doklady za měsíc v PDF, export pro Pohodu (ZIP s XML), ISDOC, CSV",
          en: "Invite the accountant; the month's documents as PDF, export for Pohoda (ZIP with XML), ISDOC, CSV",
        },
      },
      {
        task: { cs: "Vyzkoušení", en: "Trying it" },
        byHand: {
          cs: "30 dní zdarma; tarif zdarma do 5 odběratelů",
          en: "30 days free; a free plan for up to 5 customers",
        },
        zettl: {
          cs: `${trialDays} dní zdarma bez karty; pak od ${formatCzk(start.priceMonthlyCzk)} měsíčně`,
          en: `${trialDays} days free, no card; then from ${formatCzk(start.priceMonthlyCzk)} a month`,
        },
      },
    ],
    sources: {
      checkedOn: "2026-11-03",
      links: [
        { label: "fakturoid.cz", url: "https://www.fakturoid.cz/" },
        { label: "ceník", url: "https://www.fakturoid.cz/cenik" },
        { label: "náklady", url: "https://www.fakturoid.cz/naklady" },
        { label: "pro účetní", url: "https://www.fakturoid.cz/pro-ucetni" },
        {
          label: "párování plateb s bankou",
          url: "https://www.fakturoid.cz/podpora/automatizace/parovani-plateb-s-bankou",
        },
      ],
    },
    steps: [
      {
        title: { cs: "Fakturujete hlavně vy", en: "You mostly invoice" },
        text: {
          cs: "Hodně vydaných faktur, upomínky, platby od odběratelů: na to je Fakturoid stavěný.",
          en: "Lots of issued invoices, reminders, payments from customers: that is what Fakturoid is built for.",
        },
      },
      {
        title: { cs: "Hlavně platíte vy", en: "You mostly pay" },
        text: {
          cs: "Hodně plateb kartou a převodem z Fio, ke kterým účetní chce doklad: na to je stavěný Zettl.",
          en: "Lots of card and transfer payments from Fio for which your accountant wants a document: that is what Zettl is built for.",
        },
      },
      {
        title: { cs: "Obojí zároveň", en: "Both at once" },
        text: {
          cs: "Fakturovat můžete dál ve Fakturoidu a Zettl použít na platby z Fio a přijaté doklady; vystavování faktur v Zettlu (tarif Firma) nepotřebujete.",
          en: "You can keep invoicing in Fakturoid and use Zettl for Fio payments and received documents; you don't need Zettl's invoicing (Firma plan).",
        },
      },
      {
        title: { cs: "Přechod do Zettlu", en: "Moving to Zettl" },
        text: {
          cs: "Kdo chce fakturovat v Zettlu, převede klienty a faktury z exportu XLSX z Fakturoidu (tarif Firma).",
          en: "If you want to invoice in Zettl, move your clients and invoices over from Fakturoid's XLSX export (Firma plan).",
        },
      },
    ],
    notes: [
      {
        cs: "Zettl funguje jen s Fio bankou. S účtem u jiné banky je Fakturoid, který podporuje víc bank, vhodnější volba.",
        en: "Zettl works only with Fio banka. With an account at another bank, Fakturoid, which supports more banks, is the better choice.",
      },
      {
        cs: "Fakturoid uvádí ceny s DPH; Zettl není plátce DPH, takže jeho ceny jsou konečné.",
        en: "Fakturoid states its prices including VAT; Zettl is not VAT registered, so its prices are final.",
      },
      {
        cs: "Zettl nevede účetnictví ani nenahrazuje fakturační služby; připraví platby a doklady pro účetní.",
        en: "Zettl does no bookkeeping and doesn't replace invoicing services; it prepares payments and documents for the accountant.",
      },
    ],
    faq: [
      {
        id: "spolu",
        question: {
          cs: "Můžu používat Fakturoid a Zettl zároveň?",
          en: "Can I use Fakturoid and Zettl at the same time?",
        },
        answer: {
          cs: `Ano. Faktury můžete dál vystavovat ve Fakturoidu a Zettl používat na stahování plateb z Fio a párování s přijatými doklady. K tomu stačí tarif ${start.name}, který fakturaci neobsahuje.`,
          en: `Yes. You can keep issuing invoices in Fakturoid and use Zettl to download Fio payments and match them with received documents. The ${start.name} plan, which has no invoicing, is enough for that.`,
        },
      },
      {
        id: "prevod",
        question: {
          cs: "Jak převedu faktury z Fakturoidu do Zettlu?",
          en: "How do I move invoices from Fakturoid to Zettl?",
        },
        answer: {
          cs: `Ve Fakturoidu vyexportujete faktury a klienty do XLSX a v Zettlu je nahrajete přes převod z Fakturoidu. Převod je v tarifu ${firma.name}.`,
          en: `Export your invoices and clients from Fakturoid to XLSX and load them into Zettl with the Fakturoid import. The import is part of the ${firma.name} plan.`,
        },
      },
      {
        id: "jina-banka",
        question: {
          cs: "Mám účet u jiné banky než Fio. Co mi dává smysl?",
          en: "My account is at a bank other than Fio. What makes sense for me?",
        },
        answer: {
          cs: "Zettl stahuje platby jen z Fio banky, s jinou bankou vám nepomůže. Fakturoid podle svých stránek podporuje víc českých bank.",
          en: "Zettl only downloads payments from Fio banka, so with another bank it won't help you. According to its own pages, Fakturoid supports several Czech banks.",
        },
      },
    ],
    related: ["guideVsSpreadsheet", "guideMatching", "guideFioApi"],
  },

  // iDoklad, from its own public pages only (checked 2026-11-03, URLs in `sources`):
  // tagline (idoklad.cz), "Ceny jsou bez DPH", 60 days free without a card, free plan for 5
  // customers (/cenik), bank movements downloaded via bank notification e-mails for most
  // banks incl. Fio, pairing primarily by variable symbol to issued invoices
  // (/podpora/nastaveni-banka, /vlastnosti/propojeni-s-dalsimi-sluzbami), Inbox for received
  // invoices with ISDOC/QR reading and AI reading, 3 free then paid (/cenik), accountant as a
  // user, exports XML/ISDOC/Excel/CSV, Money/Pohoda/Vario (/vlastnosti/doklady-pro-ucetni).
  // No claim about what iDoklad does not do. No paid prices.
  guideIdoklad: {
    key: "guideIdoklad",
    updated: "2026-11-03",
    seo: {
      title: {
        cs: "Zettl a iDoklad: fakturace, nebo párování plateb z Fio? — Zettl",
        en: "Zettl and iDoklad: invoicing, or matching Fio payments? — Zettl",
      },
      description: {
        cs: "iDoklad je fakturační program, Zettl stahuje platby z Fio přes API a hledá k nim doklady. Kdy se hodí který a jak fungují spolu.",
        en: "iDoklad is an invoicing program; Zettl downloads Fio payments through the API and looks for their documents. When each fits, and how they work together.",
      },
    },
    name: { cs: "Zettl a iDoklad", en: "Zettl and iDoklad" },
    eyebrow: { cs: "Srovnání", en: "Comparison" },
    h1: {
      cs: "Zettl a iDoklad: fakturace, nebo párování plateb z Fio?",
      en: "Zettl and iDoklad: invoicing, or matching Fio payments?",
    },
    lead: {
      cs: "iDoklad je online fakturační program: faktury, pravidelné faktury, upomínky a evidence úhrad. Zettl každý den stáhne platby z vašeho účtu u Fio přes API a k odchozím platbám hledá účtenky a faktury. Kdo hlavně fakturuje, sáhne spíš po iDokladu; kdo potřebuje doklad ke každé platbě z Fio, po Zettlu. Spolu fungují také.",
      en: "iDoklad is an online invoicing program: invoices, recurring invoices, reminders and payment records. Zettl downloads the payments from your Fio account through the API every day and looks for receipts and invoices for the outgoing ones. If invoicing is your main job, iDoklad is the more natural fit; if you need a document for every Fio payment, Zettl is. They also work side by side.",
    },
    comparisonWith: {
      name: "iDoklad",
      title: { cs: "iDoklad a Zettl vedle sebe", en: "iDoklad and Zettl side by side" },
    },
    comparison: [
      {
        task: { cs: "Hlavní úkol", en: "Main job" },
        byHand: {
          cs: "Fakturace: faktury, pravidelné faktury, upomínky, evidence úhrad",
          en: "Invoicing: invoices, recurring invoices, reminders, payment records",
        },
        zettl: {
          cs: "Platby z Fio a doklady k nim: párování, co chybí, podklady pro účetní",
          en: "Fio payments and their documents: matching, what's missing, files for the accountant",
        },
      },
      {
        task: { cs: "Jak přijdou platby", en: "How payments arrive" },
        byHand: {
          cs: "Z e-mailových oznámení banky u většiny bank včetně Fio; páruje hlavně podle variabilního symbolu s vydanými fakturami",
          en: "From the bank's e-mail notifications, for most banks including Fio; pairs mainly by variable symbol with issued invoices",
        },
        zettl: {
          cs: "Přes Fio API každý den; páruje odchozí platby s přijatými doklady a příchozí s vydanými fakturami",
          en: "Through the Fio API every day; matches outgoing payments with received documents and incoming ones with issued invoices",
        },
      },
      {
        task: { cs: "Přijaté doklady", en: "Received documents" },
        byHand: {
          cs: "Inbox: přijaté faktury z e-mailu nebo fotky, čtení ISDOC a QR; čtení AI má několik dokladů zdarma, další placeně",
          en: "Inbox: received invoices from e-mail or a photo, reads ISDOC and QR; AI reading has a few documents free, more paid",
        },
        zettl: {
          cs: "Adresa firmy @zettl.cz; čtení AI je v tarifu do měsíčního limitu",
          en: "The company's @zettl.cz address; AI reading is in the plan up to a monthly limit",
        },
      },
      {
        task: { cs: "Pro účetní", en: "For the accountant" },
        byHand: {
          cs: "Účetní jako uživatel; export XML, ISDOC, Excel, CSV; napojení na Money, Pohodu a Vario",
          en: "The accountant as a user; XML, ISDOC, Excel, CSV export; links to Money, Pohoda and Vario",
        },
        zettl: {
          cs: "Pozvání účetní; doklady za měsíc v PDF, export pro Pohodu (ZIP s XML), ISDOC, CSV",
          en: "Invite the accountant; the month's documents as PDF, export for Pohoda (ZIP with XML), ISDOC, CSV",
        },
      },
      {
        task: { cs: "Vyzkoušení", en: "Trying it" },
        byHand: {
          cs: "60 dní zdarma bez karty; tarif zdarma pro 5 odběratelů",
          en: "60 days free, no card; a free plan for 5 customers",
        },
        zettl: {
          cs: `${trialDays} dní zdarma bez karty; pak od ${formatCzk(start.priceMonthlyCzk)} měsíčně`,
          en: `${trialDays} days free, no card; then from ${formatCzk(start.priceMonthlyCzk)} a month`,
        },
      },
    ],
    sources: {
      checkedOn: "2026-11-03",
      links: [
        { label: "idoklad.cz", url: "https://www.idoklad.cz/" },
        { label: "ceník", url: "https://www.idoklad.cz/cenik" },
        {
          label: "propojení s dalšími službami",
          url: "https://www.idoklad.cz/vlastnosti/propojeni-s-dalsimi-sluzbami",
        },
        {
          label: "doklady pro účetní",
          url: "https://www.idoklad.cz/vlastnosti/doklady-pro-ucetni",
        },
        { label: "nastavení banky", url: "https://www.idoklad.cz/podpora/nastaveni-banka" },
      ],
    },
    steps: [
      {
        title: { cs: "Fakturujete hlavně vy", en: "You mostly invoice" },
        text: {
          cs: "Hodně vydaných faktur, pravidelné faktury, upomínky: na to je iDoklad stavěný.",
          en: "Lots of issued invoices, recurring invoices, reminders: that is what iDoklad is built for.",
        },
      },
      {
        title: { cs: "Hlavně platíte vy", en: "You mostly pay" },
        text: {
          cs: "Hodně plateb kartou a převodem z Fio, ke kterým účetní chce doklad: na to je stavěný Zettl.",
          en: "Lots of card and transfer payments from Fio for which your accountant wants a document: that is what Zettl is built for.",
        },
      },
      {
        title: { cs: "Obojí zároveň", en: "Both at once" },
        text: {
          cs: `Fakturovat můžete dál v iDokladu a Zettl použít na platby z Fio a přijaté doklady; stačí tarif ${start.name} bez fakturace.`,
          en: `You can keep invoicing in iDoklad and use Zettl for Fio payments and received documents; the ${start.name} plan without invoicing is enough.`,
        },
      },
      {
        title: { cs: "Faktury v ISDOC", en: "Invoices as ISDOC" },
        text: {
          cs: `Vydané faktury z jiného programu můžete do Zettlu nahrát jako soubory ISDOC (tarif ${firma.name}).`,
          en: `You can load issued invoices from another program into Zettl as ISDOC files (${firma.name} plan).`,
        },
      },
    ],
    notes: [
      {
        cs: "Zettl funguje jen s Fio bankou. S účtem u jiné banky je vhodnější iDoklad, který podporuje víc bank.",
        en: "Zettl works only with Fio banka. With an account at another bank, iDoklad, which supports more banks, is the better choice.",
      },
      {
        cs: "iDoklad uvádí ceny bez DPH; Zettl není plátce DPH, takže jeho ceny jsou konečné.",
        en: "iDoklad states its prices excluding VAT; Zettl is not VAT registered, so its prices are final.",
      },
      {
        cs: "Zettl nevede účetnictví ani nenahrazuje fakturační programy; připraví platby a doklady pro účetní.",
        en: "Zettl does no bookkeeping and doesn't replace invoicing programs; it prepares payments and documents for the accountant.",
      },
    ],
    faq: [
      {
        id: "spolu",
        question: {
          cs: "Můžu používat iDoklad a Zettl zároveň?",
          en: "Can I use iDoklad and Zettl at the same time?",
        },
        answer: {
          cs: `Ano. Faktury můžete dál vystavovat v iDokladu a Zettl používat na stahování plateb z Fio a párování s přijatými doklady. Stačí k tomu tarif ${start.name}.`,
          en: `Yes. You can keep issuing invoices in iDoklad and use Zettl to download Fio payments and match them with received documents. The ${start.name} plan is enough for that.`,
        },
      },
      {
        id: "isdoc",
        question: {
          cs: "Dostanu faktury z iDokladu do Zettlu?",
          en: "Can I get my iDoklad invoices into Zettl?",
        },
        answer: {
          cs: `iDoklad podle svých stránek exportuje do ISDOC a Zettl umí vydané faktury z ISDOC importovat, s náhledem a přeskočením těch, které už v Zettlu jsou. Import je v tarifu ${firma.name}.`,
          en: `According to its own pages iDoklad exports to ISDOC, and Zettl can import issued invoices from ISDOC, with a preview and skipping those already in Zettl. The import is part of the ${firma.name} plan.`,
        },
      },
      {
        id: "jina-banka",
        question: {
          cs: "Mám účet u jiné banky než Fio. Co mi dává smysl?",
          en: "My account is at a bank other than Fio. What makes sense for me?",
        },
        answer: {
          cs: "Zettl stahuje platby jen z Fio banky, s jinou bankou vám nepomůže. iDoklad podle svých stránek podporuje víc českých bank.",
          en: "Zettl only downloads payments from Fio banka, so with another bank it won't help you. According to its own pages, iDoklad supports several Czech banks.",
        },
      },
    ],
    related: ["guideVsSpreadsheet", "guideMatching", "guideIsdoc"],
  },
};
